import React, { useEffect, useRef, useState, useCallback, forwardRef } from 'react';
import FloatingButtonGroup from './FloatingButtonGroup';
import ToolboxTab from './tabs/ToolboxTab';
import { uploadBothImages } from '../services/imageUploadService';
import KonvaController from '../utils/KonvaController';
import { getUrlParams, isKioskMode } from '../utils/urlUtils';
import { useTranslation, getLanguageFromUrl } from '../utils/languageUtils';
import LayerModal from './LayerModal';
import { getGlobalSelectedOptions, setGlobalSelectedOptions } from '../utils/globalOptions';
import ThreeDPreview from './ThreeDPreview';
import ExportedImagesPanel from './ExportedImagesPanel';
import CustomExportPanel from './ui/CustomExportPanel';
import './ProductView.css';
// import { getSiteStyleColors } from '../config/siteStylesConfig'; // unused
import * as weimobService from '../services/weimobService';
import { buildUploadBaseName, derivePreviewKey, derivePrintKey, buildIndexedFilename } from '../utils/uploadNaming';
import useResponsiveMode from '../utils/useResponsiveMode';
import { getDisabledViewsSet, filterViewsByLinkedView, getViewIdFromView } from '../utils/productOptionsUtils';
import { tryExportPenSetPreview } from '../utils/exportPenSetPreview';
import fontService from '../services/fontService';

const ProductView = forwardRef(({ product, baseUrl, designUrl, selectedView, onControllerReady, configData: propConfigData, options, onShowThemeSelection, onSyncSelectedView }, ref) => {
  const { t } = useTranslation();
  const { mode: responsiveMode } = useResponsiveMode();
  const isKiosk = isKioskMode();

  const containerRef = useRef(null);
  
  // Update a global attribute so CSS can switch between mobile/desktop
  useEffect(() => {
    try {
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-mode', responsiveMode);
      }
    } catch {}
  }, [responsiveMode]);
  
  // Helper function to get display name in current language
  const getDisplayName = (displayName) => {
    if (!displayName) return '';
    if (typeof displayName === 'string') {
      return displayName; // Backward compatibility
    }
    if (typeof displayName === 'object') {
      const currentLang = getLanguageFromUrl();
      return displayName[currentLang] || displayName['en'] || displayName['cn'] || '';
    }
    return '';
  };
  // (moved) useImperativeHandle is defined after local state so it can access setters
  
  const controllerRef = useRef(null);
  const lastResetAtRef = useRef(0);
  const safeReset = useCallback(() => {
    const now = Date.now();
    if (now - lastResetAtRef.current < 120) {
      console.log('[ProductView] Skipping duplicate reset within debounce window');
      return;
    }
    lastResetAtRef.current = now;
    try {
      controllerRef.current?.resetDesign?.({ keepBase: true });
    } catch (err) {
      console.warn('resetDesign failed, retrying shortly...', err);
      setTimeout(() => controllerRef.current?.resetDesign?.({ keepBase: true }), 50);
    }
  }, []);

  // Ensure WeChat Mini Program API is ready before navigation (redirectTo only)
  const waitForMiniProgramReady = useCallback((timeoutMs = 4000) => {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const tick = () => {
        try {
          const mp = typeof window !== 'undefined' && window.wx && window.wx.miniProgram ? window.wx.miniProgram : null;
          const ok = !!(mp && typeof mp.redirectTo === 'function');
          if (ok) return resolve(true);
        } catch {}
        if (Date.now() - start >= timeoutMs) return reject(new Error('MiniProgram API not ready'));
        setTimeout(tick, 120);
      };
      tick();
    });
  }, []);

  // Simple navigation using redirectTo only (avoid exceeding page stack)
  const safeNavigateToMiniCart = useCallback(async (retries = 0) => {
    const CART_URL = '/ec_cart/index';
    await waitForMiniProgramReady(4000);
    const mp = window.wx?.miniProgram;
    if (!mp || typeof mp.redirectTo !== 'function') throw new Error('miniProgram.redirectTo not available');
    console.log('[cart] using redirectTo to', CART_URL);
    mp.redirectTo({ url: CART_URL });
    return true;
  }, [waitForMiniProgramReady]);

  const [isInitialized, setIsInitialized] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFonts] = useState(false);
  const [fontLoadingError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  // Show a modal after successful add-to-cart to inform auto-redirect and offer a manual button
  const [showCartRedirectModal, setShowCartRedirectModal] = useState(false);
  const [cartRedirectCountdown, setCartRedirectCountdown] = useState(3);
  // Error modal for failed add-to-cart
  const [showCartErrorModal, setShowCartErrorModal] = useState(false);
  const [cartErrorMessage, setCartErrorMessage] = useState('');
  const lastCartArgsRef = useRef(null);
  // Always using transparent background (false) for all product exports
  const [showTestFeatures, setShowTestFeatures] = useState(false); // For test features like download button
  const [hasStickerSet, setHasStickerSet] = useState(false); // Track if Themes exist and have stickers
  const [configData, setConfigData] = useState(null); // Store product configuration data
  // Export panel settings from config (default to normal)
  const exportPanelCfg = React.useMemo(() => (configData?.ExportedImagesPanel || {}), [configData]);
  const exportPanelMode = exportPanelCfg.mode || exportPanelCfg.panelMode || 'normal';
  const [isButtonGroupCollapsed, setIsButtonGroupCollapsed] = useState(false);
  const [showThreeD, setShowThreeD] = useState(false);
  const didAutoOpenThreeDRef = useRef(false);

  const [kioskTextEditorOpen, setKioskTextEditorOpen] = useState(false);
  const [kioskTextNode, setKioskTextNode] = useState(null);
  const kioskTextNodeRef = useRef(null);
  const [kioskTextValue, setKioskTextValue] = useState('');
  const kioskTextEditorPanelRef = useRef(null);
  
  // Kiosk sticker panel state
  const [kioskStickerPanelOpen, setKioskStickerPanelOpen] = useState(false);
  const [kioskStickers, setKioskStickers] = useState([]);
  const [kioskLoadingStickers, setKioskLoadingStickers] = useState(false);
  const [kioskSelectedTheme, setKioskSelectedTheme] = useState(null);
  const kioskStickerPanelRef = useRef(null);
  const [kioskSelectedStickerSet, setKioskSelectedStickerSet] = useState(null);
  const [kioskThemePageIndex, setKioskThemePageIndex] = useState(0);

  const [kioskUploadOpen, setKioskUploadOpen] = useState(false);
  const [kioskUploadTicket, setKioskUploadTicket] = useState(null);
  const [kioskUploadQrUrl, setKioskUploadQrUrl] = useState('');
  const [kioskUploadUploadUrl, setKioskUploadUploadUrl] = useState('');
  const [kioskUploadPhotoUrls, setKioskUploadPhotoUrls] = useState([]);
  const [kioskUploadHistoryUrls, setKioskUploadHistoryUrls] = useState([]);
  const [kioskUploadLoading, setKioskUploadLoading] = useState(false);
  const [kioskUploadError, setKioskUploadError] = useState('');
  const [kioskUploadFlowActive, setKioskUploadFlowActive] = useState(false);
  const kioskUploadPollTimerRef = useRef(null);
  const kioskUploadFileInputRef = useRef(null);

  const KIOSK_UPLOAD_HISTORY_KEY = 'kiosk.upload.history.urls';

  // Constants for theme carousel
  const KIOSK_THEMES_PER_PAGE = 3;
  const [kioskFontFamily, setKioskFontFamily] = useState('Arial');
  const [kioskFontColor, setKioskFontColor] = useState('#000000');
  const kioskTextSnapshotRef = useRef(null);

  const systemFonts = React.useMemo(() => ([
    { name: 'Arial', displayName: 'Arial' },
    { name: 'Helvetica', displayName: 'Helvetica' },
    { name: 'Times New Roman', displayName: 'Times New Roman' },
    { name: 'Georgia', displayName: 'Georgia' },
    { name: 'Verdana', displayName: 'Verdana' },
    { name: 'Courier New', displayName: 'Courier New' },
    { name: 'system-ui', displayName: 'System UI' }
  ]), []);

  const [kioskFontOptions, setKioskFontOptions] = useState(systemFonts);

  const isKonvaTextNode = useCallback((node) => {
    try {
      if (!node) return false;
      if (node.className === 'Text') return true;
      if (typeof node.getClassName === 'function' && node.getClassName() === 'Text') return true;
      return false;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isKiosk) return;
    const url = propConfigData?.FontUrl || configData?.FontUrl;
    if (!url) {
      setKioskFontOptions(systemFonts);
      return;
    }

    let canceled = false;
    (async () => {
      try {
        const fonts = await fontService.loadFonts(url);
        if (canceled) return;
        const custom = Array.isArray(fonts)
          ? fonts.map(f => ({ name: f.name, displayName: f.displayName || f.name }))
          : [];
        setKioskFontOptions(custom.length ? custom : systemFonts);
      } catch (e) {
        if (canceled) return;
        setKioskFontOptions(systemFonts);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [isKiosk, propConfigData?.FontUrl, configData?.FontUrl, systemFonts]);

  useEffect(() => {
    if (!isKiosk) return;
    if (!kioskTextEditorOpen) return;
    if (!Array.isArray(kioskFontOptions) || kioskFontOptions.length === 0) return;

    let canceled = false;
    (async () => {
      for (const f of kioskFontOptions) {
        if (canceled) return;
        const name = f?.name;
        if (!name) continue;
        if (systemFonts.some(sf => sf.name === name)) continue;
        try {
          await fontService.loadFontOnDemand(name);
        } catch {}
      }
    })();

    return () => {
      canceled = true;
    };
  }, [isKiosk, kioskTextEditorOpen, kioskFontOptions, systemFonts]);

  const applyKioskTextToNode = useCallback((node, nextText) => {
    try {
      if (!isKonvaTextNode(node)) return;
      node.text(nextText);
      try { controllerRef.current?.normalizeTextAnchor?.(node); } catch {}
      try { node.getLayer()?.batchDraw?.(); } catch {}
    } catch {}
  }, [isKonvaTextNode]);

  const applyKioskStylesToNode = useCallback((node, styles = {}) => {
    try {
      if (!isKonvaTextNode(node)) return;
      if (styles.fontFamily) node.fontFamily(styles.fontFamily);
      if (styles.fill) node.fill(styles.fill);
      try { controllerRef.current?.normalizeTextAnchor?.(node); } catch {}
      try { node.getLayer()?.batchDraw?.(); } catch {}
    } catch {}
  }, [isKonvaTextNode]);

  const safeKonvaGetter = useCallback((node, key, fallback) => {
    try {
      if (!node) return fallback;
      const v = node?.[key];
      if (typeof v === 'function') return v.call(node);
      if (v !== undefined) return v;
      return fallback;
    } catch {
      return fallback;
    }
  }, []);

  const syncKioskTextSelectionFromNode = useCallback((node) => {
    try {
      if (!isKonvaTextNode(node)) return;
      kioskTextNodeRef.current = node;
      setKioskTextNode(node);

      const initialText = safeKonvaGetter(node, 'text', '');
      const initialFont = safeKonvaGetter(node, 'fontFamily', 'Arial');
      const initialFill = safeKonvaGetter(node, 'fill', '#000000');

      setKioskTextValue(initialText);
      setKioskFontFamily(initialFont);
      setKioskFontColor(initialFill);
    } catch {}
  }, [isKonvaTextNode]);

  const openKioskTextEditorForSelectedNode = useCallback(() => {
    try {
      const node = kioskTextNodeRef.current || window.selectedKonvaNode;
      if (!isKonvaTextNode(node)) return;
      syncKioskTextSelectionFromNode(node);
      setKioskTextEditorOpen(true);
    } catch {}
  }, [isKonvaTextNode, syncKioskTextSelectionFromNode]);

  useEffect(() => {
    if (!isKiosk) return;
    const onTextNodeSelected = (e) => {
      try {
        const node = e?.detail?.node || window.selectedKonvaNode;
        if (!isKonvaTextNode(node)) return;
        // Do NOT auto-open editor; just sync selection so the Text button can become Edit.
        syncKioskTextSelectionFromNode(node);
      } catch {}
    };
    const onNodeDeselected = () => {
      kioskTextNodeRef.current = null;
      setKioskTextNode(null);
    };
    const onSelectionChanged = (e) => {
      try {
        const nodeType = e?.detail?.nodeType;
        if (nodeType !== 'text') {
          kioskTextNodeRef.current = null;
          setKioskTextNode(null);
        }
      } catch {}
    };
    window.addEventListener('textNodeSelected', onTextNodeSelected);
    window.addEventListener('nodeDeselected', onNodeDeselected);
    window.addEventListener('SELECTION_CHANGED', onSelectionChanged);
    return () => {
      window.removeEventListener('textNodeSelected', onTextNodeSelected);
      window.removeEventListener('nodeDeselected', onNodeDeselected);
      window.removeEventListener('SELECTION_CHANGED', onSelectionChanged);
    };
  }, [isKiosk, isKonvaTextNode, syncKioskTextSelectionFromNode]);

  const cancelKioskTextEdit = useCallback(() => {
    try {
      const node = kioskTextNodeRef.current || kioskTextNode;
      const snap = kioskTextSnapshotRef.current;
      if (isKonvaTextNode(node) && snap) {
        // Restore snapshot (best-effort)
        try { applyKioskTextToNode(node, snap.text ?? ''); } catch {}
        try { applyKioskStylesToNode(node, { fontFamily: snap.fontFamily, fill: snap.fill }); } catch {}
      }
    } catch {}
    setKioskTextEditorOpen(false);
  }, [kioskTextNode, applyKioskTextToNode, applyKioskStylesToNode]);

  const applyKioskTextEdit = useCallback(() => {
    setKioskTextEditorOpen(false);
  }, []);

  const openKioskStickerPanel = useCallback(() => {
    setKioskStickerPanelOpen(true);
  }, []);

  const closeKioskStickerPanel = useCallback(() => {
    setKioskStickerPanelOpen(false);
  }, []);

  const closeKioskUploadModal = useCallback(() => {
    try {
      if (kioskUploadPollTimerRef.current) {
        clearInterval(kioskUploadPollTimerRef.current);
        kioskUploadPollTimerRef.current = null;
      }
    } catch {}
    setKioskUploadOpen(false);
    setKioskUploadLoading(false);
    setKioskUploadError('');
    setKioskUploadTicket(null);
    setKioskUploadQrUrl('');
    setKioskUploadUploadUrl('');
    setKioskUploadPhotoUrls([]);
    setKioskUploadHistoryUrls([]);
    setKioskUploadFlowActive(false);
  }, []);

  const openKioskUploadModal = useCallback(() => {
    setKioskUploadOpen(true);
  }, []);

  const cancelKioskUploadNow = useCallback(() => {
    try {
      if (kioskUploadPollTimerRef.current) {
        clearInterval(kioskUploadPollTimerRef.current);
        kioskUploadPollTimerRef.current = null;
      }
    } catch {}
    setKioskUploadLoading(false);
    setKioskUploadError('');
    setKioskUploadTicket(null);
    setKioskUploadQrUrl('');
    setKioskUploadUploadUrl('');
    setKioskUploadPhotoUrls([]);
    setKioskUploadFlowActive(false);
  }, []);

  const loadKioskUploadHistory = useCallback(() => {
    try {
      const raw = window?.localStorage?.getItem?.(KIOSK_UPLOAD_HISTORY_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(u => String(u)).filter(Boolean);
    } catch {
      return [];
    }
  }, []);

  const persistKioskUploadHistory = useCallback((urls) => {
    try {
      if (!window?.localStorage?.setItem) return;
      window.localStorage.setItem(KIOSK_UPLOAD_HISTORY_KEY, JSON.stringify(urls));
    } catch {}
  }, []);

  const mergeKioskUploadHistory = useCallback((newUrls) => {
    const existing = loadKioskUploadHistory();
    const all = [...(Array.isArray(newUrls) ? newUrls : []), ...existing]
      .map(u => String(u))
      .filter(Boolean);
    const deduped = Array.from(new Set(all));
    const limited = deduped.slice(0, 20);
    persistKioskUploadHistory(limited);
    setKioskUploadHistoryUrls(limited);
  }, [loadKioskUploadHistory, persistKioskUploadHistory]);

  const clearKioskUploadCache = useCallback(() => {
    try { window?.localStorage?.removeItem?.(KIOSK_UPLOAD_HISTORY_KEY); } catch {}
    setKioskUploadHistoryUrls([]);
  }, []);

  const onKioskUploadDeviceSelected = useCallback(async (e) => {
    try {
      const file = e?.target?.files && e.target.files[0] ? e.target.files[0] : null;
      if (!file) return;

      // If QR flow is active, stop it so the user sees the list immediately.
      try {
        if (kioskUploadPollTimerRef.current) {
          clearInterval(kioskUploadPollTimerRef.current);
          kioskUploadPollTimerRef.current = null;
        }
      } catch {}
      setKioskUploadFlowActive(false);
      setKioskUploadLoading(false);
      setKioskUploadError('');
      setKioskUploadTicket(null);
      setKioskUploadQrUrl('');
      setKioskUploadUploadUrl('');

      const dataUrl = await new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(new Error('read failed'));
          reader.readAsDataURL(file);
        } catch (err) {
          reject(err);
        }
      });

      if (!dataUrl) return;

      setKioskUploadPhotoUrls((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        const next = [String(dataUrl), ...arr.map(u => String(u))].filter(Boolean);
        return Array.from(new Set(next));
      });
      try {
        mergeKioskUploadHistory([String(dataUrl)]);
        // Ensure UI list updates immediately (not only after re-opening modal)
        setKioskUploadHistoryUrls((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          const next = [String(dataUrl), ...arr.map(u => String(u))].filter(Boolean);
          return Array.from(new Set(next)).slice(0, 20);
        });
      } catch {}

      try {
        if (kioskUploadFileInputRef.current) kioskUploadFileInputRef.current.value = '';
      } catch {}
    } catch {}
  }, [mergeKioskUploadHistory]);

  const startKioskUploadNow = useCallback(async () => {
    try {
      if (kioskUploadPollTimerRef.current) {
        clearInterval(kioskUploadPollTimerRef.current);
        kioskUploadPollTimerRef.current = null;
      }
    } catch {}

    setKioskUploadLoading(true);
    setKioskUploadFlowActive(true);
    setKioskUploadError('');
    setKioskUploadTicket(null);
    setKioskUploadQrUrl('');
    setKioskUploadUploadUrl('');
    setKioskUploadPhotoUrls([]);

    try {
      const response = await fetch('https://api.plugcustom.com.cn/kiosk/ImageUpload/api/create_upload', {
        method: 'GET',
        redirect: 'follow',
      });

      const json = await response.json();
      const uploadId = json?.ticket_id || json?.upload_id || json?.uploadId || json?.id;
      const uploadUrl = json?.upload_url || json?.uploadUrl || '';
      if (!uploadId) {
        throw new Error('Create upload failed');
      }
      setKioskUploadTicket(String(uploadId));
      if (uploadUrl) setKioskUploadUploadUrl(String(uploadUrl));

      // Generate QR image client-side from uploadUrl (if qrcode package is available).
      if (uploadUrl) {
        try {
          const mod = await import('qrcode');
          const QRCode = mod && (mod.default || mod);
          if (QRCode && typeof QRCode.toDataURL === 'function') {
            const dataUrl = await QRCode.toDataURL(String(uploadUrl), { margin: 1, width: 280 });
            if (dataUrl) setKioskUploadQrUrl(String(dataUrl));
          }
        } catch {
          // Fallback: show plain URL (QR image unavailable)
        }
      }
    } catch (e) {
      setKioskUploadError(e?.message ? String(e.message) : 'Create upload failed');
    } finally {
      setKioskUploadLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isKiosk) return;
    if (!kioskUploadOpen) return;
    const urls = loadKioskUploadHistory();
    setKioskUploadHistoryUrls(urls);
  }, [isKiosk, kioskUploadOpen, loadKioskUploadHistory]);

  useEffect(() => {
    if (!isKiosk) return;
    if (!kioskUploadOpen) return;
    if (!kioskUploadTicket) return;
    if (kioskUploadPhotoUrls.length > 0) return;

    try {
      if (kioskUploadPollTimerRef.current) {
        clearInterval(kioskUploadPollTimerRef.current);
        kioskUploadPollTimerRef.current = null;
      }
    } catch {}

    let canceled = false;
    const poll = async () => {
      if (canceled) return;
      try {
        const response = await fetch(`https://api.plugcustom.com.cn/kiosk/ImageUpload/api/uploads/${encodeURIComponent(String(kioskUploadTicket))}/list`, {
          method: 'GET',
          redirect: 'follow',
        });
        const json = await response.json();
        if (canceled) return;

        const items = Array.isArray(json?.items) ? json.items : [];
        const urls = items.map(it => it?.url).filter(Boolean).map(u => String(u));
        const isComplete = json?.completed === true;

        if (urls.length > 0) {
          setKioskUploadPhotoUrls(urls);
          mergeKioskUploadHistory(urls);
          // One-time QR: once photos are available, hide upload id/QR.
          setKioskUploadTicket(null);
          setKioskUploadQrUrl('');
          setKioskUploadFlowActive(false);
          try {
            if (kioskUploadPollTimerRef.current) {
              clearInterval(kioskUploadPollTimerRef.current);
              kioskUploadPollTimerRef.current = null;
            }
          } catch {}
          return;
        }

        // If the backend says it's completed but no items are returned, stop polling and show an error.
        if (isComplete) {
          setKioskUploadError('No uploads found');
          setKioskUploadFlowActive(false);
          try {
            if (kioskUploadPollTimerRef.current) {
              clearInterval(kioskUploadPollTimerRef.current);
              kioskUploadPollTimerRef.current = null;
            }
          } catch {}
        }
      } catch {
        // keep polling
      }
    };

    poll();
    kioskUploadPollTimerRef.current = setInterval(poll, 2000);
    return () => {
      canceled = true;
      try {
        if (kioskUploadPollTimerRef.current) {
          clearInterval(kioskUploadPollTimerRef.current);
          kioskUploadPollTimerRef.current = null;
        }
      } catch {}
    };
  }, [isKiosk, kioskUploadOpen, kioskUploadTicket, kioskUploadPhotoUrls.length, mergeKioskUploadHistory]);

  useEffect(() => {
    if (!isKiosk) return;
    const onCloseKioskTextOverlay = () => {
      try {
        cancelKioskTextEdit();
      } catch {}
    };
    window.addEventListener('KIOSK_CLOSE_TEXT_OVERLAY', onCloseKioskTextOverlay);
    return () => window.removeEventListener('KIOSK_CLOSE_TEXT_OVERLAY', onCloseKioskTextOverlay);
  }, [isKiosk, cancelKioskTextEdit]);

  useEffect(() => {
    if (!isKiosk) return;
    const onCloseKioskStickerOverlay = () => {
      try {
        closeKioskStickerPanel();
      } catch {}
    };
    window.addEventListener('KIOSK_CLOSE_STICKER_OVERLAY', onCloseKioskStickerOverlay);
    return () => window.removeEventListener('KIOSK_CLOSE_STICKER_OVERLAY', onCloseKioskStickerOverlay);
  }, [isKiosk, closeKioskStickerPanel]);

  // Load stickers from URL (from StickersTab.js)
  const loadKioskStickersFromUrl = useCallback(async (stickerSetUrl) => {
    setKioskLoadingStickers(true);
    try {
      if (!stickerSetUrl) throw new Error('Invalid sticker set URL');

      const response = await fetch(stickerSetUrl);
      if (!response.ok) throw new Error(`Failed to fetch stickers: ${response.status}`);

      const text = await response.text();

      const isImageUrl = (url) => {
        if (!url) return false;
        const lowerUrl = url.toLowerCase();
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
        return imageExtensions.some(ext => lowerUrl.endsWith(ext));
      };

      const lines = text.split('\n');
      const stickerUrls = lines
        .slice(1)
        .filter(line => line.trim())
        .map(line => {
          const parts = line.split(',');
          if (parts.length >= 2) {
            const url = parts[1].trim();
            return isImageUrl(url) ? url : null;
          }
          return null;
        })
        .filter(url => url);

      setKioskStickers(stickerUrls);
      return stickerUrls;
    } catch (err) {
      console.error('Error loading stickers:', err);
      setKioskStickers([]);
      return [];
    } finally {
      setKioskLoadingStickers(false);
    }
  }, []);

  // Fetch stickers for kiosk panel
  const fetchKioskStickers = useCallback(async () => {
    try {
      let stickerSetUrl = null;
      
      // Try selected sticker set first
      if (kioskSelectedStickerSet) {
        stickerSetUrl = kioskSelectedStickerSet.StickerUrl || kioskSelectedStickerSet.stickerSet?.Url || kioskSelectedStickerSet.Url;
      }
      
      // Try selected theme's sticker
      if (!stickerSetUrl && kioskSelectedTheme?.selectedSticker) {
        stickerSetUrl = kioskSelectedTheme.selectedSticker.StickerUrl || kioskSelectedTheme.selectedSticker.stickerSet?.Url || kioskSelectedTheme.selectedSticker.Url;
      }
      
      // Try theme's StickersCsv
      if (!stickerSetUrl && kioskSelectedTheme?.StickersCsv?.length > 0) {
        stickerSetUrl = kioskSelectedTheme.StickersCsv[0].Url;
      }
      
      // Fallback to first theme
      if (!stickerSetUrl && configData?.Themes?.length > 0) {
        const firstTheme = configData.Themes[0];
        if (firstTheme.StickersCsv?.length > 0) {
          stickerSetUrl = firstTheme.StickersCsv[0]?.Url;
        }
      }
      
      if (stickerSetUrl) {
        await loadKioskStickersFromUrl(stickerSetUrl);
      } else {
        setKioskStickers([]);
      }
    } catch (error) {
      console.error('Error fetching stickers:', error);
      setKioskStickers([]);
    }
  }, [configData, kioskSelectedTheme, kioskSelectedStickerSet, loadKioskStickersFromUrl]);

  // Handle theme selection (from StickersTab.js logic)
  const handleKioskThemeSelect = useCallback((theme) => {
    if (!theme) return;

    const displayThemes = getKioskThemesWithContent();
    const idx = displayThemes.findIndex(t =>
      (t.ThemeName === theme.ThemeName) || (t.id && theme.id && t.id === theme.id)
    );

    if (idx !== -1) {
      setKioskThemePageIndex(Math.floor(idx / KIOSK_THEMES_PER_PAGE));
    }

    let allSets = [];

    if (theme.StickersCsv?.length > 0) {
      allSets = allSets.concat(theme.StickersCsv.map(csv => ({
        StickerName: csv.Name,
        StickerUrl: csv.Url,
        stickerSet: { Name: csv.Name, Url: csv.Url },
        isColorSet: false,
        isBackground: false
      })));
    }

    if (theme.BackgroundsCsv?.length > 0) {
      allSets = allSets.concat(theme.BackgroundsCsv.map(csv => ({
        StickerName: csv.Name,
        StickerUrl: csv.Url,
        stickerSet: { Name: csv.Name, Url: csv.Url },
        isColorSet: false,
        isBackground: true
      })));
    }

    if (allSets.length > 0) {
      const firstSet = allSets[0];
      setKioskSelectedStickerSet(firstSet);

      const themeWithSets = {
        ...theme,
        stickerSets: allSets,
        selectedSticker: firstSet
      };

      setKioskSelectedTheme(themeWithSets);
      setKioskStickers([]); // Clear stickers to trigger reload
    } else {
      const emptyTheme = { ...theme, stickerSets: [] };
      setKioskSelectedTheme(emptyTheme);
      setKioskSelectedStickerSet(null);
    }
  }, []);

  // Handle sticker set selection
  const handleKioskStickerSetSelect = useCallback((stickerSet) => {
    if (!stickerSet) return;

    setKioskSelectedStickerSet(stickerSet);

    if (kioskSelectedTheme) {
      const updatedTheme = { ...kioskSelectedTheme, selectedSticker: stickerSet };
      setKioskSelectedTheme(updatedTheme);
    }

    setKioskStickers([]); // Clear stickers to trigger reload
  }, [kioskSelectedTheme]);

  // Get themes with content (from StickersTab.js)
  const getKioskThemesWithContent = useCallback(() => {
    if (!configData?.Themes || !Array.isArray(configData.Themes)) return [];
    return configData.Themes.filter(theme => {
      const hasStickers = Array.isArray(theme.StickersCsv) && theme.StickersCsv.length > 0;
      const hasBackgrounds = Array.isArray(theme.BackgroundsCsv) && theme.BackgroundsCsv.length > 0;
      return hasStickers || hasBackgrounds;
    });
  }, [configData]);

  // Get current page themes
  const getKioskCurrentPageThemes = useCallback(() => {
    const displayThemes = getKioskThemesWithContent();
    return displayThemes.slice(kioskThemePageIndex * KIOSK_THEMES_PER_PAGE, (kioskThemePageIndex + 1) * KIOSK_THEMES_PER_PAGE);
  }, [kioskThemePageIndex, getKioskThemesWithContent]);

  // Get total pages
  const getKioskTotalPages = useCallback(() => {
    const displayThemes = getKioskThemesWithContent();
    return Math.ceil(displayThemes.length / KIOSK_THEMES_PER_PAGE);
  }, [getKioskThemesWithContent]);

  // Theme navigation
  const handleKioskPrevThemePage = useCallback(() => {
    if (kioskThemePageIndex > 0) setKioskThemePageIndex(kioskThemePageIndex - 1);
  }, [kioskThemePageIndex]);

  const handleKioskNextThemePage = useCallback(() => {
    const totalPages = getKioskTotalPages();
    if (kioskThemePageIndex < totalPages - 1) setKioskThemePageIndex(kioskThemePageIndex + 1);
  }, [kioskThemePageIndex, getKioskTotalPages]);

  const getKioskSelectedThemeIndex = useCallback(() => {
    const themesWithContent = getKioskThemesWithContent();
    if (!themesWithContent.length) return -1;
    if (!kioskSelectedTheme) return 0;
    const idx = themesWithContent.findIndex(
      (t) => String(t?.ThemeName ?? '') === String(kioskSelectedTheme?.ThemeName ?? '')
    );
    return idx >= 0 ? idx : 0;
  }, [getKioskThemesWithContent, kioskSelectedTheme]);

  const handleKioskPrevThemeBanner = useCallback(() => {
    const themesWithContent = getKioskThemesWithContent();
    if (!themesWithContent.length) return;
    const idx = getKioskSelectedThemeIndex();
    const nextIdx = Math.max(0, (idx >= 0 ? idx : 0) - 1);
    const nextTheme = themesWithContent[nextIdx];
    if (nextTheme) handleKioskThemeSelect(nextTheme);
  }, [getKioskThemesWithContent, getKioskSelectedThemeIndex, handleKioskThemeSelect]);

  const handleKioskNextThemeBanner = useCallback(() => {
    const themesWithContent = getKioskThemesWithContent();
    if (!themesWithContent.length) return;
    const idx = getKioskSelectedThemeIndex();
    const nextIdx = Math.min(themesWithContent.length - 1, (idx >= 0 ? idx : 0) + 1);
    const nextTheme = themesWithContent[nextIdx];
    if (nextTheme) handleKioskThemeSelect(nextTheme);
  }, [getKioskThemesWithContent, getKioskSelectedThemeIndex, handleKioskThemeSelect]);

  const kioskThemeSwipeStateRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    didTrigger: false,
  });

  const handleKioskThemeBannerPointerDown = useCallback((e) => {
    try {
      const point = e?.touches?.[0] ?? e;
      if (!point) return;
      kioskThemeSwipeStateRef.current = {
        active: true,
        startX: Number(point.clientX) || 0,
        startY: Number(point.clientY) || 0,
        didTrigger: false,
      };
    } catch {}
  }, []);

  const handleKioskThemeBannerPointerMove = useCallback((e) => {
    try {
      const st = kioskThemeSwipeStateRef.current;
      if (!st?.active || st.didTrigger) return;

      const point = e?.touches?.[0] ?? e;
      if (!point) return;

      const dx = (Number(point.clientX) || 0) - st.startX;
      const dy = (Number(point.clientY) || 0) - st.startY;

      // Only treat as a swipe if it's mostly horizontal
      if (Math.abs(dx) < 30) return;
      if (Math.abs(dx) < Math.abs(dy)) return;

      st.didTrigger = true;

      if (dx < 0) {
        handleKioskNextThemeBanner();
      } else {
        handleKioskPrevThemeBanner();
      }
    } catch {}
  }, [handleKioskNextThemeBanner, handleKioskPrevThemeBanner]);

  const handleKioskThemeBannerPointerUp = useCallback(() => {
    kioskThemeSwipeStateRef.current.active = false;
  }, []);

  // Initialize with first theme (from StickersTab.js logic)
  useEffect(() => {
    const displayThemes = getKioskThemesWithContent();
    if (displayThemes.length > 0 && !kioskSelectedTheme) {
      handleKioskThemeSelect(displayThemes[0]);
    }
  }, [configData, kioskSelectedTheme, getKioskThemesWithContent, handleKioskThemeSelect]);

  // Load stickers when panel opens or theme changes
  useEffect(() => {
    if (kioskStickerPanelOpen && kioskSelectedTheme) {
      fetchKioskStickers();
    }
  }, [kioskStickerPanelOpen, kioskSelectedTheme, fetchKioskStickers]);

  // Add sticker to canvas (from StickersTab.js logic)
  const addKioskStickerToCanvas = useCallback(async (stickerUrl) => {
    if (!controllerRef.current) {
      console.error('konvaController not available');
      return;
    }
    
    try {
      const sticker = await controllerRef.current.addImage(stickerUrl, { name: 'sticker', draggable: true });
      if (!sticker) return;
      
      const stage = controllerRef.current.stage;
      if (!stage) return;
      
      const canvasWidth = stage.width();
      const width = sticker.width();
      const desiredWidth = canvasWidth * 0.10;
      const scale = desiredWidth / width;
      sticker.scaleX(scale);
      sticker.scaleY(scale);
      
      // Position sticker in center
      sticker.x((canvasWidth - width * scale) / 2);
      sticker.y((stage.height() - sticker.height() * scale) / 2);
      
      // Ensure the newly added sticker is selected
      try {
        window.selectedKonvaNode = sticker;
        window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: 'image' } }));
      } catch (e) {}
      
      try {
        if (typeof controllerRef.current.addTransform === 'function') {
          controllerRef.current.addTransform(sticker, 'Design');
        }
      } catch (e) {}
      
      sticker.getLayer().batchDraw();
      closeKioskStickerPanel();
    } catch (error) {
      console.error('Error adding sticker:', error);
    }
  }, [closeKioskStickerPanel]);

  const applyKioskFontFamilyPreview = useCallback(async (nextFontFamily) => {
    try {
      const node = kioskTextNodeRef.current || kioskTextNode;
      if (!isKonvaTextNode(node)) return;
      try {
        if (nextFontFamily && !systemFonts.some(f => f.name === nextFontFamily)) {
          await fontService.loadFontOnDemand(nextFontFamily);
        }
      } catch {}
      try { applyKioskStylesToNode(node, { fontFamily: nextFontFamily }); } catch {}
    } catch {}
  }, [kioskTextNode, applyKioskStylesToNode, systemFonts, isKonvaTextNode]);

  const didRunSelectedViewEffectRef = useRef(false);
  const [hintVisible, setHintVisible] = useState(true);
  const handleToggle3D = useCallback(() => {
    setShowThreeD((v) => {
      const next = !v;
      console.log('[ProductView] Toggling 3D preview =>', next);
      return next;
    });
  }, []);

  // (moved below after showLayerModal/openLayerModal/closeLayerModal definitions)

  const toggleButtonGroupCollapse = () => {
    setIsButtonGroupCollapsed(!isButtonGroupCollapsed);
  };
  const [showLayerModal, setShowLayerModal] = useState(false); // State for layer modal visibility

  // Helpers to open/close layer modal + broadcast events so toolbars can reflect state
  const openLayerModal = React.useCallback(() => {
    setShowLayerModal(true);
    try { window.dispatchEvent(new CustomEvent('LAYER_MODAL_OPEN')); } catch {}
  }, []);
  const closeLayerModal = React.useCallback(() => {
    setShowLayerModal(false);
    try { window.dispatchEvent(new CustomEvent('LAYER_MODAL_CLOSE')); } catch {}
  }, []);

  // Listen for Layer modal toggle requests from toolbars that don't have direct handlers
  useEffect(() => {
    const onRequestLayerToggle = () => {
      if (showLayerModal) {
        closeLayerModal();
      } else {
        openLayerModal();
      }
    };
    window.addEventListener('REQUEST_LAYER_MODAL_TOGGLE', onRequestLayerToggle);
    return () => window.removeEventListener('REQUEST_LAYER_MODAL_TOGGLE', onRequestLayerToggle);
  }, [showLayerModal, openLayerModal, closeLayerModal]);

  // Expose an imperative API so outer components can open the local layer modal
  React.useImperativeHandle(ref, () => ({
    openLayerModal: openLayerModal,
    getController: () => controllerRef.current,
  }));
  
  // Track previous view-affecting option subset to decide reloads
  const prevViewOptionsRef = useRef(null);

  // Helpers to read view-affecting option keys from config and pick those from a selectedOptions object
  const getViewOptionKeys = useCallback(() => {
    try {
      const optionsCfg = propConfigData?.SupportedModels?.Options || {};
      return Object.keys(optionsCfg)
        .filter(k => optionsCfg[k] && (optionsCfg[k].isVisualOption === true || optionsCfg[k].isViewOption === true));
    } catch {
      return [];
    }
  }, [propConfigData]);

  const pickViewOptions = useCallback((selectedOptions) => {
    const keys = getViewOptionKeys();
    const result = {};
    keys.forEach(k => {
      if (selectedOptions && Object.prototype.hasOwnProperty.call(selectedOptions, k)) {
        result[k] = selectedOptions[k];
      }
    });
    return result;
  }, [getViewOptionKeys]);

  const shallowEqual = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    for (const k of ak) {
      const av = a[k]?.value ?? a[k];
      const bv = b[k]?.value ?? b[k];
      if (av !== bv) return false;
    }
    return true;
  };

  // Listen for global selected options changes and only reload when view-affecting subset changed
  useEffect(() => {
    const onGlobalOptionsChanged = (e) => {
      try {
        const selectedOptions = e?.detail?.selectedOptions || {};
        // Always store latest options on controller for save/load context
        if (controllerRef.current) {
          controllerRef.current.currentOptions = selectedOptions;
        }
      } catch (err) {
        console.warn('onGlobalOptionsChanged handling failed', err);
      }
    };

    window.addEventListener('globalOptionsChanged', onGlobalOptionsChanged);
    return () => window.removeEventListener('globalOptionsChanged', onGlobalOptionsChanged);
  }, [pickViewOptions]);
  
  // State for exported images
  const exportedImages = {
    preview: null,
    design: null
  };
  const [showExportedImages, setShowExportedImages] = useState(false);
  // Confirmation modal visibility for Add to Cart flow
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allViewsImages, setAllViewsImages] = useState([]);
  // If user starts Add to Cart from the Export Panel, reuse these images instead of re-exporting
  const [pendingImagesForCart, setPendingImagesForCart] = useState(null);
  // Track design counts per view to enforce maxDesignCount limits
  const [designCounts, setDesignCounts] = useState({});

  // Helper: compute design limits for a view based on the ACTIVE VARIANT's DesignProduct config
  const getVariantDesignLimits = useCallback((viewId) => {
    try {
      const view = viewId && (typeof viewId === 'string' ? viewId : viewId.id);
      if (!view) return { viewId: '', maxDesignCount: undefined, allowDesignIndex: undefined };

      // 1) Prefer globally computed activeVariant from HeaderBar (most authoritative)
      let variant = null;
      try {
        if (typeof window !== 'undefined' && window.activeVariant && window.activeVariant.DesignProduct) {
          variant = window.activeVariant;
        }
      } catch {}

      // 2) If no window.activeVariant, attempt positional Option1..3 matching (legacy)
      if (!variant) {
        const opts = getGlobalSelectedOptions?.() || {};
        const opt1 = opts?.Option1?.value ?? opts?.Option1 ?? opts?.option1;
        const opt2 = opts?.Option2?.value ?? opts?.Option2 ?? opts?.option2;
        const opt3 = opts?.Option3?.value ?? opts?.Option3 ?? opts?.option3;
        const variants = propConfigData?.variants || propConfigData?.Variants || [];
        variant = variants.find(v => {
          const v1 = v?.Option1 || v?.option1;
          const v2 = v?.Option2 || v?.option2;
          const v3 = v?.Option3 || v?.option3;
          const ok1 = opt1 ? v1 === opt1 : true;
          const ok2 = opt2 ? v2 === opt2 : true;
          const ok3 = opt3 ? v3 === opt3 : true;
          return ok1 && ok2 && ok3;
        }) || null;
      }

      // 3) As a robust fallback, match by selected option VALUES across Option1..6 regardless of key names
      if (!variant) {
        const opts = getGlobalSelectedOptions?.() || {};
        const selectedValues = Object.values(opts).map(v => (v && v.value != null ? v.value : v)).filter(Boolean);
        const svSet = new Set(selectedValues);
        const variants = propConfigData?.variants || propConfigData?.Variants || [];
        variant = variants.find(v => {
          const vv = [v.Option1, v.Option2, v.Option3, v.Option4, v.Option5, v.Option6]
            .map(x => (x == null ? '' : String(x)))
            .filter(Boolean);
          // selected values must be a subset of the variant's option values
          for (const val of svSet) {
            if (!vv.includes(String(val))) return false;
          }
          return true;
        }) || null;
        if (variant) {
          try { console.log('[getVariantDesignLimits] Fallback matched variant by values'); } catch {}
        }
      }

      const dp = variant?.DesignProduct?.[view];
      if (!dp) {
        return { viewId: view, maxDesignCount: undefined, allowDesignIndex: undefined };
      }

      if (typeof dp === 'string') {
        // String template: unlimited unless constrained elsewhere
        return { viewId: view, maxDesignCount: undefined, allowDesignIndex: undefined };
      }

      const allow = Array.isArray(dp.allowDesign_index) ? dp.allowDesign_index : undefined;
      const max = typeof dp.maxDesignCount === 'number' ? dp.maxDesignCount : undefined;
      return { viewId: view, maxDesignCount: max, allowDesignIndex: allow };
    } catch (e) {
      console.warn('getVariantDesignLimits failed, treating as unlimited', e);
      return { viewId: typeof viewId === 'string' ? viewId : (viewId?.id || ''), maxDesignCount: undefined, allowDesignIndex: undefined };
    }
  }, [propConfigData]);

  const incrementDesignCount = (viewId) => {
    setDesignCounts((prevDesignCounts) => {
      const nextDesignCounts = { ...prevDesignCounts };
      nextDesignCounts[viewId] = (nextDesignCounts[viewId] || 0) + 1;
      console.log(`📊 Design count for view ${viewId} incremented to ${nextDesignCounts[viewId]}`);
      return nextDesignCounts;
    });
  };

  const hasReachedMaxDesignCount = useCallback((viewId, showFeedback = false) => {
    if (!viewId) return false;

    const limits = getVariantDesignLimits(viewId);
    const maxDesignCount = limits.maxDesignCount; // undefined => unlimited
    if (!maxDesignCount) return false;

    const currentCount = designCounts[viewId] || 0;
    const hasReached = currentCount >= maxDesignCount;

    console.log(`🔍 Checking maxDesignCount for view ${viewId}: ${currentCount}/${maxDesignCount} (${hasReached ? 'LIMIT REACHED' : 'limit not reached'})`);

    if (hasReached && showFeedback) {
      const viewsList = propConfigData?.SupportedModels?.CommonProperties?.Views || [];
      const viewConfig = viewsList.find(v => (typeof v === 'object' ? v.id === viewId : v === viewId));
      const viewName = viewConfig && typeof viewConfig.name === 'object'
        ? (viewConfig.name[getLanguageFromUrl()] || viewConfig.name.en || viewId)
        : ((viewConfig && viewConfig.name) || viewId);

      setStatusMessage(`Maximum design limit reached (${currentCount}/${maxDesignCount}) for ${viewName}`);
      setTimeout(() => setStatusMessage(''), 5000);
    }

    return hasReached;
  }, [propConfigData, designCounts, setStatusMessage, getVariantDesignLimits]);

  // Check for test parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');
    setShowTestFeatures(testParam === 'true');
    
  }, []);

  // Listen for global 3D toggle events (fallback for tabs that don't receive handler)
  useEffect(() => {
    const onGlobalToggle3D = () => {
      setShowThreeD((v) => {
        const next = !v;
        console.log('[ProductView] Global TOGGLE_3D_PREVIEW =>', next);
        return next;
      });
    };
    window.addEventListener('TOGGLE_3D_PREVIEW', onGlobalToggle3D);
    return () => window.removeEventListener('TOGGLE_3D_PREVIEW', onGlobalToggle3D);
  }, []);

  // Default open 3D preview on first load for 3D products (desktop + mobile)
  useEffect(() => {
    if (!configData || !configData.is3DProduct) return;
    if (didAutoOpenThreeDRef.current) return;
    didAutoOpenThreeDRef.current = true;
    setShowThreeD(true);
  }, [configData]);

  // When container dimensions change (via ResizeObserver), just resize the canvas without reinitializing
  useEffect(() => {
    if (!controllerRef.current?.stage || !containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    if (containerWidth > 0) {
      controllerRef.current.resizeCanvas(containerWidth);
    }
  }, [dimensions.width, dimensions.height]);

  // When selectedView changes, auto-close modal/3D and broadcast a VIEW_CHANGED event so toolbars reset
  useEffect(() => {
    if (!selectedView) return;
    // On initial mount / initial selectedView setup, don't immediately close the default-open 3D preview.
    if (!didRunSelectedViewEffectRef.current) {
      didRunSelectedViewEffectRef.current = true;
      try { window.dispatchEvent(new CustomEvent('VIEW_CHANGED')); } catch {}
      console.log('[ProductView] VIEW_CHANGED dispatched due to initial selectedView setup');
      return;
    }
    try {
      // Close layer modal if open
      if (showLayerModal) {
        setShowLayerModal(false);
        try { window.dispatchEvent(new CustomEvent('LAYER_MODAL_CLOSE')); } catch {}
      }
      // Turn off 3D preview if it was on
      if (showThreeD) {
        setShowThreeD(false);
        try { window.dispatchEvent(new CustomEvent('THREED_STATE', { detail: { active: false } })); } catch {}
      }
      // Notify listeners that view changed
      window.dispatchEvent(new CustomEvent('VIEW_CHANGED'));
      console.log('[ProductView] VIEW_CHANGED dispatched due to selectedView update');
      // Ensure canvas resets user design on view change (keep base layers)
      console.log('[ProductView] View changed -> resetting design (keepBase: true)');
      safeReset();
    } catch {}
  }, [selectedView]);

  // Broadcast 3D state so any toolbar instance can update its active UI
  useEffect(() => {
    try { window.dispatchEvent(new CustomEvent('THREED_STATE', { detail: { active: showThreeD } })); } catch {}
  }, [showThreeD]);

  // On design index change: reset and then load the NEW index's design (no save here)
  useEffect(() => {
    const onDesignIndexChanged = (e) => {
      const detail = e?.detail || {};
      console.log('[ProductView] DESIGN_INDEX_CHANGED -> resetting then loading new index (keepBase: true)', detail);
      safeReset();
      // Load for the new index after reset
      setTimeout(() => {
        try {
          if (controllerRef.current?.productId) {
            console.log('[ProductView] DESIGN_INDEX_CHANGED -> loading design for new index');
            controllerRef.current.loadDesign(controllerRef.current.selectedView || selectedView);
          }
        } catch (err) {
          console.warn('loadDesign after DESIGN_INDEX_CHANGED failed', err);
        }
      }, 50);
    };
    window.addEventListener('DESIGN_INDEX_CHANGED', onDesignIndexChanged);
    return () => window.removeEventListener('DESIGN_INDEX_CHANGED', onDesignIndexChanged);
  }, []);

  // Save current design BEFORE index change request from HeaderBar
  useEffect(() => {
    const onSaveBeforeIndexChange = (e) => {
      const detail = e?.detail || {};
      console.log('[ProductView] SAVE_DESIGN_BEFORE_INDEX_CHANGE -> saving under prev index', detail);
      try {
        if (controllerRef.current?.productId) {
          controllerRef.current.saveDesign(controllerRef.current.selectedView || selectedView);
        }
      } catch (err) {
        console.warn('saveDesign failed on SAVE_DESIGN_BEFORE_INDEX_CHANGE, continuing...', err);
      }
    };
    window.addEventListener('SAVE_DESIGN_BEFORE_INDEX_CHANGE', onSaveBeforeIndexChange);
    return () => window.removeEventListener('SAVE_DESIGN_BEFORE_INDEX_CHANGE', onSaveBeforeIndexChange);
  }, []);

  // Also handle explicit reset requests from other components (e.g., HeaderBar)
  useEffect(() => {
    const onRequestResetDesign = (e) => {
      const detail = e?.detail || {};
      console.log('[ProductView] REQUEST_RESET_DESIGN -> resetting design (keepBase: true)', detail);
      safeReset();
    };
    window.addEventListener('REQUEST_RESET_DESIGN', onRequestResetDesign);
    return () => window.removeEventListener('REQUEST_RESET_DESIGN', onRequestResetDesign);
  }, []);

  // Use configData from props if available or fetch it as a fallback
  useEffect(() => {
    const processConfigData = async () => {
      try {
        // If we have configData from props, use that
        if (propConfigData) {
          
          setConfigData(propConfigData);
          
          // Check for DesignUrl which will be used to overlay on product
          if (propConfigData.hasOwnProperty('DesignUrl')) {
            // Check if DesignUrl exists and is not empty
            // This determines if we should load the Design image
            if (propConfigData.DesignUrl.trim() !== '') {
              
            } else {
              
            }
          } else {
            console.error('DesignUrl not found in config');
            // If DesignUrl is not in the config, don't load Design image
          }
        }
        
        // Using DesignUrl presence to determine if a design overlay should be loaded
      } catch (error) {
        console.error('Error processing config:', error);
        // If there's an error, still allow the component to render with default values
      }
    };
    
    processConfigData();
  }, [product, propConfigData]);

  // Track container dimensions
  useEffect(() => {
    if (!containerRef.current) return;

    let mounted = true;
    let observer = null;
    let timeoutId = null;

    const updateDimensions = () => {
      if (!mounted || !containerRef.current) return;
      
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const isMobile = window.innerWidth <= 768;
      
      // Calculate dimensions based on container's aspect ratio
      let width, height;
      
      if (isMobile) {
        // Square aspect ratio (1:1) on mobile
        width = Math.min(containerWidth, window.outerWidth * 0.7);
        height = width; // 1:1 aspect ratio
      } else {
        // 4:3 aspect ratio on desktop
        width = containerWidth;
        height = (width * 3) / 4; // 4:3 aspect ratio
      }
      
      setDimensions(current => {
        // Only update if dimensions actually changed
        if (current.width !== width || current.height !== height) {
          return { width, height };
        }
        return current;
      });
    };

    // Initial update
    updateDimensions();

    // Set up resize observer for better performance
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    // Fallback: check again after a delay
    timeoutId = setTimeout(updateDimensions, 100);

    return () => {
      mounted = false;
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (observer) {
        observer.disconnect();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []); // No dependencies - runs once on mount

  // Initialize Konva controller when dimensions are available
  // Track if the canvas has been initialized to prevent unnecessary resizing
  const [controllerReady, setControllerReady] = useState(false);

  // Single consolidated useEffect for controller initialization and product loading
  useEffect(() => {
    // Initialize when product and container are ready. Width/height are derived from DOM below.
    if (!product || !containerRef.current) {
      return;
    }
    
    
    
    const initializeController = async () => {
      // Clean up existing controller first
      if (controllerRef.current?.destroy) {
        
        controllerRef.current.destroy();
        controllerRef.current = null;
      }
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const width = Math.max(containerRect.width, 1);
      const height = Math.max(containerRect.height, 1);
      
      // Only proceed if we have valid dimensions
      if (width <= 1 || height <= 1) {
        console.warn('Container dimensions are too small, retrying...');
        setTimeout(initializeController, 100);
        return;
      }
      
      try {
        // Initialize the controller
        const controller = new KonvaController(containerRef.current);
        
        // Initialize with configuration object
        controller.init({
          width,
          height,
          backgroundColor: 'var(--site-canvas-bg)',
          preserveAspectRatio: true
        });
        
        // Get current options with priority: controller > global > create defaults
        const globalOptions = getGlobalSelectedOptions();
        let currentOptions = controllerRef.current?.getCurrentOptions() || globalOptions || {};
        
        // If no options exist anywhere, create default options from product configuration
        if (!currentOptions || Object.keys(currentOptions).length === 0) {
          const defaultOptions = {};
          const cfgOptions = propConfigData?.SupportedModels?.Options || product?.Options || {};
          const ordered = Object.entries(cfgOptions)
            .filter(([, def]) => def && Array.isArray(def.values) && def.values.length > 0)
            .sort(([a],[b]) => {
              const na = parseInt(String(a).replace(/[^0-9]/g, ''), 10) || 0;
              const nb = parseInt(String(b).replace(/[^0-9]/g, ''), 10) || 0;
              return na - nb;
            });
          for (const [key, def] of ordered) {
            defaultOptions[key] = { value: def.values[0].value ?? def.values[0] };
          }
          currentOptions = defaultOptions;
          console.log('🎯 Created default options for initialization:', currentOptions);
          
          // Set the default options globally so they're available everywhere
          setGlobalSelectedOptions(currentOptions);
        }
        
        
        // Store important references in the controller
        controller.baseUrl = baseUrl;
        controller.productId = product.ProductionName;
        controller.selectedView = selectedView;
        controller.currentOptions = currentOptions;
        // Seed previous view-affecting subset to avoid triggering a reload immediately after init
        try {
          prevViewOptionsRef.current = pickViewOptions(currentOptions || {});
        } catch {}
        // Pass full product config data to controller for export decisions (e.g., variant ExportMasked)
        controller.configData = propConfigData;
        
        // Set the controller ref BEFORE calling onControllerReady
        controllerRef.current = controller;
        
        
        
        // Notify parent that controller is ready
        if (onControllerReady) {
          onControllerReady(controller);
        }
        
        // Mark controller as ready
        setControllerReady(true);
        
        // Skip font preloading during initialization - fonts will be loaded on-demand when needed
        // This improves initial load performance and uses system fonts for UI
        console.log('📝 Font loading skipped during initialization - using system fonts for UI');
        
        // Get design limits from the ACTIVE VARIANT's DesignProduct for this view
        const viewId = typeof selectedView === 'string' ? selectedView : selectedView.id;
        const limits = getVariantDesignLimits(viewId);
        const currentCount = designCounts[viewId] || 0;

        // Log the maximum design count information prominently (aligned with controller)
        console.log('🔢 MAX DESIGN COUNT FOR VIEW:', {
          viewId,
          viewName: (propConfigData?.SupportedModels?.CommonProperties?.Views?.find(v => (typeof v === 'object' ? v.id === viewId : v === viewId))?.name) || viewId,
          maxDesignCount: limits.maxDesignCount ?? 'UNLIMITED',
          currentCount,
          remaining: limits.maxDesignCount ? `${limits.maxDesignCount - currentCount}/${limits.maxDesignCount}` : 'UNLIMITED',
          allowSpecs: limits.allowDesignIndex
        });

        console.log('📊 View configuration:', {
          view: selectedView,
          viewId,
          maxDesignCount: limits.maxDesignCount ?? 'Not defined',
          currentDesignCount: currentCount,
          remainingDesigns: limits.maxDesignCount ? limits.maxDesignCount - currentCount : 'Unlimited',
          allowSpecs: limits.allowDesignIndex
        });
        
        // Now load the product if baseUrl is available
        if (baseUrl) {
          const hasDesign = !!designUrl;
          const caseUrl = propConfigData?.CaseUrl || null;
          
          
          await controller.loadProduct(
            product.ProductionName, 
            baseUrl, 
            hasDesign, 
            designUrl || null, 
            selectedView,
            caseUrl
          );
          try { controller.setHintVisible(hintVisible); } catch {}
          
          
        } else {
          console.log('ProductView: No baseUrl provided, skipping product loading');
        }
        
      } catch (error) {
        console.error('ProductView: Error initializing controller or loading product:', error);
        setControllerReady(false);
      } finally {
        // Always mark as initialized to hide loading message
        setIsInitialized(true);
      }
    };
    
    // Reset controller ready state
    setControllerReady(false);
    
    // Start initialization
    initializeController();
    
    // Cleanup function
    return () => {
      if (controllerRef.current?.destroy) {
        
        controllerRef.current.destroy();
        controllerRef.current = null;
      }
      setControllerReady(false);
    };
  }, [product, baseUrl, designUrl, selectedView, onControllerReady, propConfigData?.CaseUrl, propConfigData?.FontUrl, propConfigData?.SupportedModels?.Options]);

  // Debug log when controller ready state changes
  useEffect(() => {
    console.log('ProductView: Controller ready state changed:', controllerReady);
  }, [controllerReady]);

  // Handle window resize for mobile detection and canvas resizing
  useEffect(() => {
    // Keep track of resize timeout to debounce
    let resizeTimeout = null;
    
    const handleResize = () => {
      // Update mobile detection immediately
      setIsMobile(window.innerWidth <= 768);
      
      // Debounce the canvas resize to avoid excessive updates
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      // Use a timeout to debounce resize events
      resizeTimeout = setTimeout(() => {
        // Only resize if controller is ready and has stage
        if (controllerRef.current?.stage && containerRef.current) {
          console.log('Window resized, adjusting canvas width');
          
          // Get container width
          const containerWidth = containerRef.current.clientWidth;
          
          // Resize canvas to container width while maintaining aspect ratio
          // Do not update React dimensions state here to avoid re-initialization loops
          controllerRef.current.resizeCanvas(containerWidth);
        }
      }, 250); // Wait 250ms after resize stops before updating
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Add event listener for saving design before view change
  useEffect(() => {
    // Function to handle saving design before view change
    const handleSaveDesignBeforeViewChange = (event) => {
      if (controllerRef.current && controllerRef.current.productId) {
        // Save the current design using the CURRENT view ID (not the new one)
        // saveDesign now uses global options and current productId automatically
        controllerRef.current.saveDesign(
          controllerRef.current.selectedView || selectedView
        );
      }
    };
    
    // Add event listener
    window.addEventListener('saveDesignBeforeViewChange', handleSaveDesignBeforeViewChange);
    
    // Remove event listener on cleanup
    return () => {
      window.removeEventListener('saveDesignBeforeViewChange', handleSaveDesignBeforeViewChange);
    };
  }, [product, selectedView]);

  // Function to export images for all views and return the allImages object
  // This function can be used by both handleDownload and processAddToCart
  const exportAllViews = async () => {
    if (!controllerRef.current) return null;

    try {
      // Signal export mode globally so controller behaves accordingly
      if (typeof window !== 'undefined') window.__isExporting = true;
      const currentView = selectedView;

      // Special case: DeliverPrint pen set preview export (colorSwatchGridQty)
      const penRes = await tryExportPenSetPreview({
        controller: controllerRef.current,
        currentView,
        configData: propConfigData,
        getGlobalSelectedOptions,
        setGlobalSelectedOptions,
      });
      if (penRes) return penRes;

      // Save the original view's design before starting export (ensure correct parameter)
      if (controllerRef.current.productId && currentView) {
        try {
          await controllerRef.current.saveDesign(
            controllerRef.current.selectedView || currentView
          );
        } catch (e) {
          console.warn('saveDesign failed before export, continuing...', e);
        }
      }

      // Collect product views and dedupe by logical view id (not by object reference)
      const viewsArray = product?.views || product?.Views || [currentView];
      const viewMap = new Map();
      viewsArray.forEach((v, idx) => {
        const id = getViewId(v) || `view_${idx + 1}`;
        if (!viewMap.has(id)) viewMap.set(id, v);
      });

      const tempAllViewsImages = [];
      const allImages = { previews: [], designs: [] };

      function getViewId(view) {
        if (!view) return '';
        if (typeof view === 'string') return view;
        if (typeof view === 'object' && view.id) return view.id;
        return String(view);
      }

      // Process in fixed product order regardless of current selection
      const currentViewId = getViewId(currentView);
      // Apply linked_view filtering to skip disabled views
      const optionsCfg = propConfigData?.SupportedModels?.Options;
      const selectedOptions = (getGlobalSelectedOptions && getGlobalSelectedOptions()) || {};
      const disabledSet = getDisabledViewsSet(optionsCfg, selectedOptions);
      let viewsToProcess = Array.from(viewMap.values()).filter(v => !disabledSet.has(getViewId(v)));
      const currentViewIdSafe = getViewId(currentView);
      if (!viewsToProcess.some(v => getViewId(v) === currentViewIdSafe)) {
        viewsToProcess = [currentView, ...viewsToProcess];
      }
      if (viewsToProcess.length === 0) {
        viewsToProcess = [currentView];
      }

      const processed = new Set();

      // Precompute total number of export jobs (sum of indices per view)
      const computeIndicesCount = (view) => {
        try {
          const vid = getViewId(view);
          const limits = getVariantDesignLimits(vid);
          const allowedSpecs = Array.isArray(limits.allowDesignIndex) ? limits.allowDesignIndex : [];
          const allowedSet = new Set();
          const parseRange = (spec) => {
            const s = String(spec || '').trim();
            if (!s) return;
            if (s.includes('-')) {
              const [a, b] = s.split('-').map(n => parseInt(n.trim(), 10));
              if (isFinite(a) && isFinite(b) && a > 0 && b >= a) {
                for (let x = a; x <= b; x++) allowedSet.add(x);
              }
            } else {
              const v = parseInt(s, 10);
              if (isFinite(v) && v > 0) allowedSet.add(v);
            }
          };
          allowedSpecs.forEach(parseRange);

          // Always export all indices up to maxDesignCount (UI-only filtering is ignored here)
          let maxDesignCount = typeof limits.maxDesignCount === 'number' && limits.maxDesignCount > 0
            ? limits.maxDesignCount
            : undefined;
          if (!maxDesignCount && allowedSet.size > 0) {
            maxDesignCount = Math.max(...allowedSet);
          }
          if (!maxDesignCount) maxDesignCount = 1;
          return maxDesignCount;
        } catch (e) {
          return 1; // safe fallback
        }
      };
      const totalJobs = viewsToProcess.reduce((sum, v) => sum + computeIndicesCount(v), 0);
      let completedJobs = 0;

      for (let i = 0; i < viewsToProcess.length; i++) {
        const view = viewsToProcess[i];
        const viewId = getViewId(view);
        if (processed.has(viewId)) continue;
        processed.add(viewId);

        // Ensure we are on the target view (compare against current active, not original)
        const activeBefore = getViewId(controllerRef.current.selectedView);
        try {
          // If different view is active, switch first
          if (activeBefore !== viewId) {
            console.log('➡️ Switching to view for export:', {
              targetView: viewId,
              currentViewBefore: activeBefore,
              globalBefore: getViewId(window.selectedView)
            });
            controllerRef.current.clearDesignLayer();
            await controllerRef.current.setSelectedView(view);
            await new Promise(r => setTimeout(r, 600));
          }

          // wait until product image for the view is ready
          if (controllerRef.current.waitForViewReady) {
            await controllerRef.current.waitForViewReady(viewId, 4000);
          }
          // Load any saved design for this view so export includes user design
          if (controllerRef.current.loadDesign) {
            await controllerRef.current.loadDesign(view);
            await new Promise(r => setTimeout(r, 100));
          }

          // Determine design index list for this view from active variant config
          const limits = getVariantDesignLimits(viewId);
          const allowedSpecs = Array.isArray(limits.allowDesignIndex) ? limits.allowDesignIndex : [];
          const allowedSet = new Set();
          const parseRange = (spec) => {
            const s = String(spec || '').trim();
            if (!s) return;
            if (s.includes('-')) {
              const [a, b] = s.split('-').map(n => parseInt(n.trim(), 10));
              if (isFinite(a) && isFinite(b) && a > 0 && b >= a) {
                for (let x = a; x <= b; x++) allowedSet.add(x);
              }
            } else {
              const v = parseInt(s, 10);
              if (isFinite(v) && v > 0) allowedSet.add(v);
            }
          };
          allowedSpecs.forEach(parseRange);

          let maxDesignCount = typeof limits.maxDesignCount === 'number' && limits.maxDesignCount > 0
            ? limits.maxDesignCount
            : undefined;
          if (!maxDesignCount && allowedSet.size > 0) {
            maxDesignCount = Math.max(...allowedSet);
          }
          if (!maxDesignCount) maxDesignCount = 1;

          // Build index iteration list: export all from 1..maxDesignCount
          const indices = Array.from({ length: maxDesignCount }, (_, k) => k + 1);

          for (const designIndex of indices) {
            try {
              // Set global design index for this view so controller picks it up
              if (typeof window !== 'undefined') {
                window.designIndexByView = window.designIndexByView || {};
                window.designIndexByView[viewId] = designIndex;
                window.currentDesignIndex = designIndex;
              }

              // Give listeners a short moment to react to the index change
              await new Promise(r => setTimeout(r, 30));

              // Refresh variant-specific layer for this index (prefer targeted updater)
              if (controllerRef.current.updateVariantDesignLayer) {
                await controllerRef.current.updateVariantDesignLayer(viewId);
                // allow a frame for compositing
                await new Promise(r => requestAnimationFrame(() => r()));
              } else {
                // Fallback: hard refresh the design layer for this view
                try { controllerRef.current.clearDesignLayer?.(); } catch {}
                if (controllerRef.current.loadDesign) {
                  await controllerRef.current.loadDesign(view);
                  await new Promise(r => setTimeout(r, 50));
                }
              }

              // Ensure user design for this specific index is loaded before export
              // Clear user design layer to avoid stacking, then load saved nodes for current index
              try { controllerRef.current.clearDesignLayer?.(); } catch {}
              if (controllerRef.current.loadDesign) {
                await controllerRef.current.loadDesign(view);
                // brief pause to ensure nodes are composited
                await new Promise(r => setTimeout(r, 30));
              }

              // Export
              const exportResult = await controllerRef.current.exportProduct({});

              // Collect for panel and upload
              tempAllViewsImages.push({
                view,
                designIndex,
                preview: exportResult?.preview || null,
                design: exportResult?.design || null,
              });

              const dateStr = new Date().toISOString().slice(0, 10);
              const productName = getDisplayName(product?.DisplayName) || 'product';
              const viewName = viewId || 'default';

              if (exportResult?.preview) {
                allImages.previews.push({
                  url: exportResult.preview,
                  filename: `${productName}_${viewName}_idx${designIndex}_preview-${dateStr}.png`,
                });
              }
              if (exportResult?.design) {
                allImages.designs.push({
                  url: exportResult.design,
                  filename: `${productName}_${viewName}_idx${designIndex}_design-${dateStr}.png`,
                });
              }

              // Update export progress status
              completedJobs += 1;
              try { setStatusMessage(`${t('status.exporting')} ${completedJobs}/${totalJobs}`); } catch {}
            } catch (e) {
              console.warn('Export failed for view/index, continuing...', { viewId, designIndex, error: e });
            }
          }
        } catch (e) {
          console.warn('Export failed for view, continuing...', { viewId, error: e });
        }
      }

      // Restore original view if it's not currently active
      const activeFinalId = getViewId(controllerRef.current.selectedView);
      if (activeFinalId !== currentViewId) {
        try {
          controllerRef.current.clearDesignLayer();
          await controllerRef.current.setSelectedView(currentView);
          await new Promise(r => setTimeout(r, 300));
          // Restore original view's design on canvas
          if (controllerRef.current.loadDesign) {
            await controllerRef.current.loadDesign(currentView);
          }
        } catch (e) {
          console.warn('Failed to restore original view', e);
        }
      }

      // Ensure app-level state/header syncs with the canvas view after export
      if (typeof onSyncSelectedView === 'function') {
        try { onSyncSelectedView(currentView); } catch (e) {}
      }

      return { allImages, tempAllViewsImages };
    } catch (error) {
      console.error('Error during export process:', error);
      setStatusMessage(t('error.exportingImages'));
      return null;
    }
    finally {
      // Always clear export flag
      if (typeof window !== 'undefined') delete window.__isExporting;
    }
  };

  // Removed duplicate handleAddToCart function
  
  const handlePreview = async () => {
    if (!controllerRef.current) return;
    // Print active variant id at the beginning
    try {
      const vid = (typeof window !== 'undefined' && window.activeVariantId) ? window.activeVariantId : null;
      const vobj = (typeof window !== 'undefined' && window.activeVariant) ? window.activeVariant : null;
      console.log('[handlePreview] activeVariantId:', vid, 'variant:', vobj);
    } catch (_) {}
    try {
      // Reset images and open panel immediately
      setAllViewsImages([]);
      setShowExportedImages(true);
      setIsExporting(true);
      setStatusMessage(t('status.preparingExport'));
      
      // Start export
      const result = await exportAllViews();
      
      if (!result || !result.tempAllViewsImages || result.tempAllViewsImages.length === 0) {
        setStatusMessage(t('status.noImagesToExport'));
        setTimeout(() => setStatusMessage(''), 3000);
        return;
      }
      
      // Update the allViewsImages state with the exported images
      setAllViewsImages(result.tempAllViewsImages);
      setStatusMessage(t('status.exportReady'));
      setTimeout(() => setStatusMessage(''), 3000);
    }
    catch (error) {
      console.error('Error during export:', error);
      setStatusMessage(t('error.preparingExport'));
      setTimeout(() => setStatusMessage(''), 5000);
    } finally {
      setIsExporting(false);
    }
  };

  const handleToggleHintLayer = () => {
    const next = !hintVisible;
    setHintVisible(next);
    try {
      controllerRef.current?.setHintVisible?.(next);
    } catch {}
  };

  // Show confirmation popup (default entry point - no pre-exported images provided)
  const handleAddToCart = () => {
    setPendingImagesForCart(null);
    // Ensure export panel is closed when showing confirmation
    setShowExportedImages(false);
    setShowConfirmation(true);
  };

  // Start add-to-cart using images already exported in the panel (skip confirmation)
  const startAddToCartWithExisting = () => {
    const images = Array.isArray(allViewsImages) && allViewsImages.length > 0
      ? allViewsImages
      : null;
    // Close the export panel immediately
    setShowExportedImages(false);
    // Directly process add-to-cart with existing images
    processAddToCart({ images });
  };

  // Export all views and upload their images (or reuse existing exported images)
  const UploadFile = async (existingImages) => {
    try {
      let imagesToUpload = Array.isArray(existingImages) && existingImages.length > 0
        ? existingImages
        : null;

      // If no existing images provided, export now
      if (!imagesToUpload) {
        setStatusMessage(t('status.preparingExport'));
        const exportRes = await exportAllViews();
        if (!exportRes || !exportRes.tempAllViewsImages || exportRes.tempAllViewsImages.length === 0) {
          console.warn('No images to upload');
          return null;
        }
        imagesToUpload = exportRes.tempAllViewsImages;
      }

      const { user } = getUrlParams();

      // Upload progress tracking
      const totalUploads = Array.isArray(imagesToUpload) ? imagesToUpload.length : 0;
      let uploadedCount = 0;

      // Helper to get a stable view id
      const getViewId = (view) => {
        if (!view) return '';
        if (typeof view === 'string') return view;
        if (typeof view === 'object' && view.id) return view.id;
        return String(view);
      };

      setStatusMessage(t('status.uploadingAllDesigns'));
      const allUploadedImages = [];
      const timestamp = Date.now(); 
      for (let i = 0; i < imagesToUpload.length; i++) {
        const item = imagesToUpload[i];
        const viewId = getViewId(item.view) || `view_${i + 1}`;

        const designIndex = item?.designIndex || 1;
        //create 2 digits random number
        const random = Math.floor(Math.random() * 100);
        // Build a shared base name and derived keys using helpers
        // Include index suffix to avoid collisions when multiple designs per view
        const base = buildUploadBaseName(viewId, timestamp);
        const objectBaseName = `${base}_${designIndex}`;
        const previewObjectKey = derivePreviewKey(objectBaseName);
        const printObjectKey = derivePrintKey(objectBaseName);
        const objectName = objectBaseName; // Backward compatibility: API still receives base name
        try {
          // Update upload progress status before each upload
          try { setStatusMessage(`${t('status.uploading')} ${i + 1}/${totalUploads}`); } catch {}
          const uploadRes = await uploadBothImages(
            item.design,
            item.preview,
            objectName,
            user
          );

          allUploadedImages.push({
            viewId,
            designIndex,
            designUrl: uploadRes.designUrl,
            previewUrl: uploadRes.previewUrl,
            objectName: uploadRes.objectName,
            objectBaseName,
            previewObjectKey,
            printObjectKey,
            previewFileName: buildIndexedFilename('preview', designIndex),
            printFileName: buildIndexedFilename('printing', designIndex),
          });
          // Increment count on success
          uploadedCount += 1;
        } catch (e) {
          console.error('Upload failed for view:', viewId, e);
          // Continue uploading other views but indicate failure by returning null overall
          return null;
        }
      }

      return { allUploadedImages };
    } catch (e) {
      console.error('UploadFile error:', e);
      return null;
    }
  };

  // Send message to Shopify parent or call Weimob add_to_cart in WeChat
  const add_to_cart = async ({ variantId, allUploadedImages, properties }) => {
    try {
      // Save args for potential retry from error modal
      lastCartArgsRef.current = { variantId, allUploadedImages };
      // Build only the available options from activeVariant (no empty Option6, etc.)
      const av = (typeof window !== 'undefined' && window.activeVariant) ? window.activeVariant : {};
      const variantOption = {};
      for (let i = 1; i <= 6; i++) {
        const key = `Option${i}`;
        const val = av ? av[key] : undefined;
        if (val !== undefined && val !== null && val !== '') variantOption[key] = val;
      }
      
      setStatusMessage(t('status.addingToCart'));

      // const isWeChat = typeof window !== 'undefined' && !!window.wx && !!window.wx.miniProgram;
      // let weimobResult = null;

      // if (isWeChat) {
      //   // Build preview_and_design_list keyed by color and view
      //   let previewAndDesignList = {};
      //   try {
      //     console.log('[add_to_cart] variantOption:', variantOption);
      //     for (const img of allUploadedImages || []) {
      //       const viewKey = img?.viewId || 'default';
      //       const key = `${viewKey}`;
      //       previewAndDesignList[key] = {
      //         designUrl: img?.designUrl,
      //         previewUrl: img?.previewUrl,
      //       };
      //     }
      //   } catch (e) {
      //     console.warn('Failed building preview_and_design_list:', e);
      //   }
      //   const cloudCustomStr = JSON.stringify({
      //     preview_and_design_list: previewAndDesignList,
      //     variantOption: variantOption,

      //   });

      //   // Resolve numeric IDs from URL or config (prefer JSON config)
      //   const { wid: widParam, pid: pidParam, app_id: appIdParam, vid: vidStoreIdParam } = getUrlParams();
      //   const cfg = (configData || propConfigData || product) || {};
      //   const vidStoreId = (cfg?.store?.id ?? cfg?.storeId ?? cfg?.VID ?? cfg?.vid ?? vidStoreIdParam) || undefined;
      //   const goodsId = (cfg?.product?.id ?? cfg?.id ?? pidParam) || undefined;
      //   const bizWid = widParam || undefined;

        
      //   console.log('[add_to_cart] Derived IDs', {
      //     hasConfigData: !!configData,
      //     hasPropConfigData: !!propConfigData,
      //     hasProduct: !!product,
      //     cfgStoreId: cfg?.store?.id,
      //     vidStoreId,
      //     goodsId,
      //     bizWid,
      //   });

      //   // Compute skuId by matching selected options flagged as isSKU=true to cfg.variants
      //   let computedSkuId = window.activeVariant?.id;
      //   if (!computedSkuId) {
      //     alert('No skuId found');
      //     return;
      //   }
      //   try {
      //     weimobResult = await weimobService.addToCart({
      //       vid: vidStoreId ? Number(vidStoreId) : undefined,
      //       goodsId: goodsId ? Number(goodsId) : undefined,
      //       skuId: computedSkuId ? Number(computedSkuId) : undefined,
      //       bizWid: bizWid ? Number(bizWid) : undefined,
      //       cloudCustom: cloudCustomStr,
      //     });
      //   } catch (err) {
      //     console.error('Weimob add_to_cart error:', err);
      //     weimobResult = { success: false, message: err?.message || String(err) };
      //   }

      //   if (weimobResult?.success || weimobResult?.status) {
      //     setStatusMessage(t('status.addedToCart'));
      //     // Show redirect modal with manual action and start an auto-redirect countdown
      //     try {
      //       setShowCartRedirectModal(true);
      //       setCartRedirectCountdown(3);
      //       // countdown timer
      //       let n = 3;
      //       const id = setInterval(() => {
      //         n -= 1;
      //         setCartRedirectCountdown(n);
      //         if (n <= 0) {
      //           clearInterval(id);
      //         }
      //       }, 1000);
      //       // Attempt auto-redirect after a short delay (fire-and-forget)
      //       setTimeout(() => {
      //         safeNavigateToMiniCart(3).catch((navErr) => {
      //           console.warn('WeChat auto-navigation failed (after retries):', navErr);
      //         });
      //       }, 1200);
      //     } catch (navErr) {
      //       console.warn('WeChat navigation setup failed:', navErr);
      //     }
      //   } else {
      //     console.error('Weimob add_to_cart failed:', weimobResult);
      //     setStatusMessage(t('error.failedToAddToCart'));
      //     setCartErrorMessage(t('error.failedToAddToCart', 'Failed to add to cart. Please try again.'));
      //     setShowCartErrorModal(true);
      //   }
      //   return weimobResult || { success: false };
      // }

      // Shopify embedded app: post a message to parent
      const payload = {
        command: 'add_to_cart',
        variantId,
        images: allUploadedImages,
        productId: product?.ProductionName || '',
        timestamp: Date.now(),
        properties: properties || undefined,
      };
      
      console.log(' [Shopify] Sending add_to_cart to parent window:', {
        variantId,
        imageCount: allUploadedImages?.length,
        productId: product?.ProductionName,
        hasParent: !!window?.parent,
        isInIframe: window !== window.parent,
      });
      console.log(' [Shopify] Full payload:', payload);
      
      try {
        if (window?.parent) {
          window.parent.postMessage(payload, '*');
          console.log(' [Shopify] postMessage sent successfully');
        } else {
          console.warn(' [Shopify] No parent window found - not in iframe?');
        }
      } catch (e) {
        console.error(' [Shopify] postMessage to parent failed:', e);
        setCartErrorMessage(t('error.failedToAddToCart', 'Failed to add to cart. Please try again.'));
        setShowCartErrorModal(true);
        return { success: false };
      }
      setStatusMessage(t('status.addedToCart'));
      return { success: true };
    } catch (e) {
      console.error('add_to_cart error:', e);
      setStatusMessage(t('error.failedToAddToCart'));
      return { success: false, error: e?.message || String(e) };
    }
  };

  const add_group_to_cart = async ({ items, allUploadedImages }) => {
    try {
      const safeItems = Array.isArray(items) ? items : [];
      const totalQty = safeItems.reduce((s, it) => s + (Number(it && it.quantity) || 1), 0);
      const first = safeItems[0] || {};
      const firstVariantId = String(first.variantId || '');
      const parts = safeItems.map(function(it) {
        var color = it && it.properties && it.properties.Color ? String(it.properties.Color) : '';
        var size = it && it.properties && it.properties.Size ? String(it.properties.Size) : '';
        var remark = it && it.properties && it.properties.order_remark ? String(it.properties.order_remark) : '';
        if (remark) return remark;
        return size ? (color + ' ' + size + ' x ' + (Number(it.quantity || 1))) : (color + ' x ' + (Number(it.quantity || 1)));
      });
      const properties = { _batch: Date.now(), order_remark: parts.join(', ') };
      const payload = {
        type: 'ADD_TO_CART',
        payload: {
          variantId: firstVariantId,
          quantity: totalQty,
          allViews: allUploadedImages,
          properties: properties
        }
      };
      if (typeof window !== 'undefined' && window.parent) {
        window.parent.postMessage(payload, '*');
        setStatusMessage(t('status.addedToCart'));
        return { success: true };
      }
      return { success: false };
    } catch (e) {
      console.error('add_group_to_cart error:', e);
      return { success: false };
    }
  };

  // Orchestrate export, upload and add-to-cart
  const processAddToCart = async (opts = {}) => {
    try {
      console.log('[DEBUG] processAddToCart:start', { opts });
      // Hide confirmation and start loading
      setShowConfirmation(false);
      setIsLoading(true);
      // Generate a per-batch client id (yyMMddHHmmss + 6 digits)
      const pad = (n) => String(n).padStart(2, '0');
      const d = new Date();
      const yy = String(d.getFullYear()).slice(-2);
      const MM = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mm = pad(d.getMinutes());
      const ss = pad(d.getSeconds());
      const rnd = Math.floor(100000 + Math.random() * 900000);
      const batchClientId = `${yy}${MM}${dd}${hh}${mm}${ss}${rnd}`;
      const imagesFromParam = Array.isArray(opts.images) && opts.images.length > 0 ? opts.images : null;
      const imagesToUse = imagesFromParam || (Array.isArray(pendingImagesForCart) && pendingImagesForCart.length > 0 ? pendingImagesForCart : null);
      console.log('[DEBUG] processAddToCart:batch', { batchClientId, imagesFromParamCount: imagesFromParam ? imagesFromParam.length : 0, pendingImagesForCartCount: Array.isArray(pendingImagesForCart) ? pendingImagesForCart.length : 0 });
      // If we already have exported images, skip the extra export status
      setStatusMessage(imagesToUse ? t('status.addingToCart') : t('status.preparingExport'));

      // Determine environment
      const isWeChat = typeof window !== 'undefined' && !!window.wx && !!window.wx.miniProgram &&
        !!new URLSearchParams(window.location.search).get('wid');
      // Variant must come ONLY from window.activeVariant
      let variantId = undefined;
      if (!isWeChat) {
        try {
          const av = (typeof window !== 'undefined') ? window.activeVariant : null;
          const id = av && (av.id != null ? av.id : undefined);
          if (id != null) {
            variantId = id;
            console.log('[DEBUG] processAddToCart:variant(win)', { variantId });
          } else {
            // Fallback: derive variant from config and selected options
            const cfg = (configData || propConfigData || product) || {};
            const optionsCfg = cfg?.SupportedModels?.Options || {};
            let skuKeys = Object.keys(optionsCfg).filter(k => optionsCfg[k]?.isSKU);
            if ((!skuKeys || skuKeys.length === 0) && Array.isArray(cfg?.variants) && cfg.variants.length > 0) {
              const allKeys = Object.keys(cfg.variants[0] || {});
              const optionKeys = allKeys.filter(k => /^Option\d+$/i.test(k));
              const distinctMap = {};
              for (const k of optionKeys) {
                const vals = new Set(
                  cfg.variants
                    .map(v => v?.[k])
                    .filter(v => v !== undefined && v !== null && String(v).trim() !== '')
                );
                if (vals.size > 1) distinctMap[k] = true;
              }
              skuKeys = optionKeys.filter(k => distinctMap[k]);
              if (skuKeys.length === 0) skuKeys = optionKeys.filter(k => /^(Option1|Option2)$/i.test(k));
            }
            const currentOptions = (controllerRef.current && controllerRef.current.getCurrentOptions && controllerRef.current.getCurrentOptions())
              || getGlobalSelectedOptions()
              || {};
            const getSelectedValue = (key) => {
              const v = currentOptions?.[key];
              if (v && typeof v === 'object' && 'value' in v) return v.value;
              return v ?? currentOptions?.[key?.toLowerCase?.()];
            };
            const selectedSku = (skuKeys || []).reduce((acc, key) => {
              const val = getSelectedValue(key);
              if (val !== undefined && val !== null && val !== '') acc[key] = val;
              return acc;
            }, {});
            const match = Array.isArray(cfg?.variants) ? cfg.variants.find(v => {
              for (const key of skuKeys) {
                const need = selectedSku[key];
                if (need === undefined) continue;
                const vv = v?.[key] ?? v?.[key?.toLowerCase?.()];
                if (String(vv) !== String(need)) return false;
              }
              return true;
            }) : null;
            if (match?.id != null) {
              variantId = String(match.id);
              console.log('[DEBUG] processAddToCart:variant(fallback)', { variantId });
            } else if (Array.isArray(cfg?.variants) && cfg.variants.length > 0 && cfg.variants[0]?.id != null) {
              variantId = String(cfg.variants[0].id);
              console.log('[DEBUG] processAddToCart:variant(default-first)', { variantId });
            } else {
              setStatusMessage('Please select a variant first');
              setIsLoading(false);
              return;
            }
          }
        } catch (e) {
          setStatusMessage('Please select a variant first');
          setIsLoading(false);
          return;
        }
      }

      // For WeChat, pre-compute skuId BEFORE exporting to avoid wasted work if selection is invalid
      let wechatSkuId;
      if (isWeChat) {
        try {
          const cfg = (configData || propConfigData || product) || {};
          // Prefer HeaderBar-exposed active variant id
          const winSku = (typeof window !== 'undefined' && window.activeVariantId) ? window.activeVariantId : null;
          if (winSku) {
            wechatSkuId = Number(winSku);
          } else {
            // Minimal inference using OptionN keys present and with selections
            const currentOptions = (controllerRef.current && controllerRef.current.getCurrentOptions && controllerRef.current.getCurrentOptions())
              || getGlobalSelectedOptions()
              || {};
            const optionsCfg = cfg?.SupportedModels?.Options || {};
            let skuKeys = Object.keys(optionsCfg).filter(k => optionsCfg[k]?.isSKU);
            if ((!skuKeys || skuKeys.length === 0) && Array.isArray(cfg?.variants) && cfg.variants.length > 0) {
              const allKeys = Object.keys(cfg.variants[0] || {});
              const optionKeys = allKeys.filter(k => /^Option\d+$/i.test(k));
              const distinctMap = {};
              for (const k of optionKeys) {
                const vals = new Set(
                  cfg.variants
                    .map(v => v?.[k])
                    .filter(v => v !== undefined && v !== null && String(v).trim() !== '')
                );
                if (vals.size > 1) distinctMap[k] = true;
              }
              skuKeys = optionKeys.filter(k => distinctMap[k]);
              if (skuKeys.length === 0) skuKeys = optionKeys.filter(k => /^(Option1|Option2)$/i.test(k));
            }
            const getSelectedValue = (key) => {
              const v = currentOptions?.[key];
              if (v && typeof v === 'object' && 'value' in v) return v.value;
              return v ?? currentOptions?.[key?.toLowerCase?.()];
            };
            const selectedSku = (skuKeys || []).reduce((acc, key) => {
              const val = getSelectedValue(key);
              if (val !== undefined && val !== null && val !== '') acc[key] = val;
              return acc;
            }, {});
            const match = Array.isArray(cfg?.variants) ? cfg.variants.find(v => {
              for (const key of skuKeys) {
                const need = selectedSku[key];
                if (need === undefined) continue;
                const vv = v?.[key] ?? v?.[key?.toLowerCase?.()];
                if (vv !== need) return false;
              }
              return true;
            }) : null;
            if (match?.id != null) wechatSkuId = Number(match.id);
          }
        } catch (e) {
          console.warn('WeChat pre-check skuId failed:', e);
        }
        // if (!wechatSkuId) {
        //   setIsLoading(false);
        //   setStatusMessage(t('error.invalidSelection'));
        //   try { alert('Please select required options to determine variant.'); } catch {}
        //   return;
        // }
      }

      

      // Upload using existing images if available (otherwise will export internally)
      const uploadRes = await UploadFile(imagesToUse, { clientId: batchClientId });
      console.log('[DEBUG] processAddToCart:uploadRes', { ok: !!uploadRes, count: uploadRes?.allUploadedImages?.length });
      if (!uploadRes || !uploadRes.allUploadedImages || uploadRes.allUploadedImages.length === 0) {
        setStatusMessage(t('error.preparingExport'));
        setIsLoading(false);
        return;
      }
      const { allUploadedImages } = uploadRes;
      console.log('[DEBUG] processAddToCart:imagesReady', { count: allUploadedImages?.length });

      // Local test mode: still call add_to_cart to test the flow, but it won't actually add to cart without parent window
      const isLocalTestMode = !!showTestFeatures;
      
      // Build order_remark from ColorQtyMap
      const qtyMap = (getGlobalSelectedOptions()?.ColorQtyMap?.value) || (window.ColorQtyMap) || null;
      let remarkStr = '';
      try {
        const opt1 = (configData || propConfigData)?.SupportedModels?.Options?.Option1;
        const vals = Array.isArray(opt1?.values) ? opt1.values : [];
        if (qtyMap && typeof qtyMap === 'object') {
          const parts = Object.entries(qtyMap)
            .filter(([, q]) => Number(q) > 0)
            .map(([valKey, q]) => {
              const meta = vals.find(v => String(v.value).toLowerCase() === String(valKey).toLowerCase());
              const label = meta && meta.displayName ? (typeof meta.displayName === 'string' ? meta.displayName : (meta.displayName?.en || meta.displayName?.cn || String(valKey))) : String(valKey);
              return `${label} x ${Number(q)}`;
            });
          remarkStr = parts.join(', ');
        }
      } catch {}

      const batchEnabled = !!(configData && configData.BatchEnable);
      const batchMap = (getGlobalSelectedOptions()?.BatchQtyMatrix?.value) || (window.BatchQtyMatrix) || null;
      const entries = batchMap && typeof batchMap === 'object' ? Object.entries(batchMap).filter(([, q]) => Number(q) > 0) : [];
      let addRes;
      if (batchEnabled && entries.length > 0) {
        const optionsDefs = (configData?.SupportedModels?.Options) || {};
        const findKeyByName = (n) => {
          const nn = String(n || '').toLowerCase();
          for (const [k, def] of Object.entries(optionsDefs)) {
            const nm = String(def?.name || '').toLowerCase();
            if (nm === nn) return k;
            const dn = typeof def?.displayName === 'string' ? def.displayName : (def?.displayName?.en || def?.displayName?.cn || '');
            if (String(dn).toLowerCase() === nn) return k;
          }
          return null;
        };
        const colorKey = findKeyByName('color') || null;
        const sizeKey = findKeyByName('size') || null;
        const getLabel = (def, val) => {
          if (!def || !Array.isArray(def.values)) return String(val);
          const meta = def.values.find(v => String(v.value).toLowerCase() === String(val).toLowerCase());
          if (!meta) return String(val);
          const dn = meta.displayName;
          if (typeof dn === 'string') return dn;
          return dn?.en || dn?.cn || String(val);
        };
        if (!sizeKey) {
          const qtyMap = (getGlobalSelectedOptions()?.ColorQtyMap?.value) || (window.ColorQtyMap) || null;
          let remarkStrLocal = '';
          try {
            const opt1 = (configData || propConfigData)?.SupportedModels?.Options?.Option1;
            const vals = Array.isArray(opt1?.values) ? opt1.values : [];
            if (qtyMap && typeof qtyMap === 'object') {
              const partsR = Object.entries(qtyMap)
                .filter(([, q]) => Number(q) > 0)
                .map(([valKey, q]) => {
                  const meta = vals.find(v => String(v.value).toLowerCase() === String(valKey).toLowerCase());
                  const label = meta && meta.displayName ? (typeof meta.displayName === 'string' ? meta.displayName : (meta.displayName?.en || meta.displayName?.cn || String(valKey))) : String(valKey);
                  return `${label} x ${Number(q)}`;
                });
              remarkStrLocal = partsR.join(', ');
            }
          } catch {}
          addRes = await add_to_cart({ variantId, allUploadedImages, properties: remarkStrLocal ? { order_remark: remarkStrLocal } : undefined });
        } else {
          const items = entries.map(([key, q]) => {
            const parts = String(key).split('|');
            const color = parts[0] || '';
            const size = parts[1] || '';
            const colorLabel = colorKey ? getLabel(optionsDefs[colorKey], color) : color;
            const sizeLabel = sizeKey && size !== 'SIZE_NA' ? getLabel(optionsDefs[sizeKey], size) : '';
            const props = { Color: color };
            if (size && size !== 'SIZE_NA') props.Size = size;
            const remarkText = sizeLabel ? `${colorLabel} ${sizeLabel} x ${Number(q)}` : `${colorLabel} x ${Number(q)}`;
            props.order_remark = remarkText;
            return {
              variantId,
              quantity: Number(q),
              properties: props,
            };
          });
          addRes = await add_group_to_cart({ items, allUploadedImages });
        }
      } else {
        const cMap = (getGlobalSelectedOptions()?.ColorQtyMap?.value) || (window.ColorQtyMap) || null;
        const cEntries = cMap && typeof cMap === 'object' ? Object.entries(cMap).filter(([, q]) => Number(q) > 0) : [];
        if (cEntries.length > 0) {
          let remarkStrLocal = '';
          let totalQty = 0;
          try {
            const opt1 = (configData || propConfigData)?.SupportedModels?.Options?.Option1;
            const vals = Array.isArray(opt1?.values) ? opt1.values : [];
            const partsR = cEntries.map(([valKey, q]) => {
              const meta = vals.find(v => String(v.value).toLowerCase() === String(valKey).toLowerCase());
              const label = meta && meta.displayName ? (typeof meta.displayName === 'string' ? meta.displayName : (meta.displayName?.en || meta.displayName?.cn || String(valKey))) : String(valKey);
              return `${label} x ${Number(q)}`;
            });
            remarkStrLocal = partsR.join(', ');
            totalQty = cEntries.reduce((s, [, q]) => s + Number(q || 0), 0);
          } catch {}
          const singleProps = remarkStrLocal ? { order_remark: remarkStrLocal } : undefined;
          const withBoxQty = singleProps ? { ...singleProps, BoxTotalQty: String(totalQty || 0) } : { BoxTotalQty: String(totalQty || 0) };
          addRes = await add_to_cart({ variantId, allUploadedImages, properties: withBoxQty });
        } else {
          addRes = await add_to_cart({ variantId, allUploadedImages, properties: remarkStr ? { order_remark: remarkStr } : undefined });
        }
      }
      
      if (isLocalTestMode) {
        // In test mode, show upload complete message
        setStatusMessage(t('status.testModeUploadComplete') + ' (Check console for postMessage logs)');
        setTimeout(() => setIsLoading(false), 2000);
      } else {
        // In production mode, handle response normally
        if (addRes && (addRes.success || addRes.status)) {
          // Keep loading briefly so user can see message
          setTimeout(() => setIsLoading(false), 1500);
        } else {
          setIsLoading(false);
        }
      }
      // Clear pending images after attempt
      setPendingImagesForCart(null);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setStatusMessage(t('error.failedToAddToCart'));
      setIsLoading(false);
      setPendingImagesForCart(null);
    }
  };

  // Listen for global add-to-cart requests (e.g., ToolboxTab fallback)
  React.useEffect(() => {
    const onRequestAddToCart = () => {
      handleAddToCart();
    };
    window.addEventListener('REQUEST_ADD_TO_CART', onRequestAddToCart);
    return () => window.removeEventListener('REQUEST_ADD_TO_CART', onRequestAddToCart);
  }, []);

  // Listen for header Done -> preview first
  React.useEffect(() => {
    const onRequestDownloadPreview = () => {
      handlePreview();
    };
    window.addEventListener('REQUEST_DOWNLOAD_PREVIEW', onRequestDownloadPreview);
    return () => window.removeEventListener('REQUEST_DOWNLOAD_PREVIEW', onRequestDownloadPreview);
  }, [handlePreview]);

  const handleMoveLayer = (direction) => {
    if (!controllerRef.current) return;
    if (direction === 'up') {
      controllerRef.current.moveUp();
    } else if (direction === 'down') {
      controllerRef.current.moveDown();
    }
  };

  const cancelAddToCart = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="product-view-container">
      {!isInitialized && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>
            {isLoadingFonts ? t('text.loadingFonts') : t('status.loadingProduct')}
            {fontLoadingError && (
              <div className="error-message">
                {t('general.warning')}: {fontLoadingError} - {t('text.fontMayNotDisplayCorrectly')}
              </div>
            )}
          </p>
        </div>
      )}
      
      {/* Add to Cart Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>{statusMessage || t('status.loading', 'Loading...')}</p>
          </div>
        </div>
      )}

      {/* Kiosk Add to Cart Button */}
      {isKiosk && !showExportedImages && !kioskUploadOpen && !kioskStickerPanelOpen && !kioskTextEditorOpen && (
        <button
          className="kiosk-add-to-cart-button"
          onClick={handlePreview}
          disabled={isLoading}
        >
          {t('product.addToCart', 'Add to Cart')}
        </button>
      )}

      {/* Add-to-Cart success modal with auto-redirect hint and manual fallback */}
      {showCartRedirectModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1000003,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: '18px',
              minWidth: 280,
              maxWidth: 420,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
              {t('status.addedToCart')} ✔
            </div>
            <div style={{ color: '#333', marginBottom: 12 }}>
              {t('status.redirectingToCart', 'Added to cart. Redirecting to cart...')}
            </div>
            <div style={{ color: '#555', marginBottom: 16 }}>
              {t('status.redirectCountdown', 'You will be redirected in')} {cartRedirectCountdown}s
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCartRedirectModal(false)}
                style={{
                  background: '#f5f5f5',
                  border: '1px solid #e5e5e5',
                  borderRadius: 8,
                  padding: '8px 12px',
                  cursor: 'pointer'
                }}
              >
                {t('general.stay', 'Stay')}
              </button>
              <button
                onClick={async () => {
                  try {
                    await safeNavigateToMiniCart(3);
                  } catch (e) {
                    console.warn('Go to Cart navigation failed:', e);
                    try { alert(`${t('error.navigateToCartFailed', 'Failed to open cart. Please tap the cart tab manually.')}\n\n${e?.message || e}`); } catch {}
                  }
                }}
                style={{
                  background: '#111',
                  color: '#fff',
                  border: '1px solid #111',
                  borderRadius: 8,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  minWidth: 120
                }}
              >
                {t('status.goToCartNow', 'Go to Cart Now')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add to Cart Confirmation Popup */}
      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-popup">
            <h3>{t('cart.title')}</h3>
            <p>{t('cart.confirmMessage')}</p>
            <div className="confirmation-buttons">
              <button 
                className="confirm-button" 
                onClick={processAddToCart}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  marginRight: '10px',
                  cursor: 'pointer'
                }}
              >
                {t('cart.confirmYes')}
              </button>
              <button 
                className="cancel-button" 
                onClick={() => setShowConfirmation(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                {t('cart.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="canvas-container">
        {isKiosk && (
          <div className="kiosk-control-overlay">
            <div className="kiosk-control-item left" style={{ top: '50%' }}>
              <button 
                type="button" 
                className="kiosk-control-plus" 
                tabIndex={-1}
                onClick={openKioskStickerPanel}
              >
                +
              </button>
              <div className="kiosk-control-line" />
              <div className="kiosk-control-label">Design</div>
            </div>

            <div className="kiosk-control-item right" style={{ top: '35%' }}>
              <button 
                type="button" 
                className="kiosk-control-plus" 
                tabIndex={-1}
                onClick={openKioskUploadModal}
              >
                +
              </button>
              <div className="kiosk-control-line" />
              <div className="kiosk-control-label">CASE DESIGN</div>
            </div>

            <div className="kiosk-control-item right" style={{ top: '65%' }}>
              <button
                type="button"
                className="kiosk-control-plus"
                disabled={kioskTextEditorOpen}
                aria-disabled={kioskTextEditorOpen}
                onClick={() => {
                  if (kioskTextEditorOpen) return;
                  if (kioskTextNodeRef.current && isKonvaTextNode(kioskTextNodeRef.current)) {
                    openKioskTextEditorForSelectedNode();
                    return;
                  }
                  const c = controllerRef.current;
                  if (!c || typeof c.addText !== 'function') return;
                  c.addText('YOUR TEXT');
                }}
              >
                {kioskTextNode ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                ) : (
                  '+'
                )}
              </button>
              <div className="kiosk-control-line" />
              <div className="kiosk-control-label">{kioskTextNode ? 'Edit' : 'Text'}</div>
            </div>
          </div>
        )}

        {isKiosk && kioskTextEditorOpen && (
          <div
            className="kiosk-text-editor-overlay"
            role="dialog"
            aria-label="Text editor"
          >
            <div ref={kioskTextEditorPanelRef} className="kiosk-text-editor-panel" onClick={(e) => e.stopPropagation()}>
              <div className="kiosk-text-editor-body">
                <input
                  className="kiosk-text-editor-input"
                  type="text"
                  value={kioskTextValue}
                  style={{
                    fontFamily: kioskFontFamily ? `"${kioskFontFamily}"` : undefined,
                    color: kioskFontColor,
                  }}
                  onChange={(e) => {
                    const next = e.target.value;
                    setKioskTextValue(next);
                    applyKioskTextToNode(kioskTextNodeRef.current || kioskTextNode, next);
                  }}
                />

                <input
                  className="kiosk-text-editor-color"
                  type="color"
                  value={kioskFontColor}
                  style={{ backgroundColor: kioskFontColor }}
                  onChange={(e) => {
                    const next = e.target.value;
                    setKioskFontColor(next);
                    applyKioskStylesToNode(kioskTextNodeRef.current || kioskTextNode, { fill: next });
                  }}
                />

                <div className="kiosk-text-editor-font-list" role="listbox" aria-label="Font family">
                  {kioskFontOptions.map((f) => {
                    const isSelected = String(f.name) === String(kioskFontFamily);
                    return (
                      <button
                        key={f.name}
                        type="button"
                        className={`kiosk-text-editor-font-pill${isSelected ? ' selected' : ''}`}
                        style={{ fontFamily: f?.name ? `"${f.name}"` : undefined }}
                        onClick={() => {
                          const next = f.name;
                          setKioskFontFamily(next);
                          applyKioskFontFamilyPreview(next);
                        }}
                      >
                        ABC
                      </button>
                    );
                  })}
                </div>

                <div className="kiosk-text-editor-actions">
                  <button
                    type="button"
                    className="kiosk-text-editor-btn close"
                    onClick={cancelKioskTextEdit}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="kiosk-text-editor-btn apply"
                    onClick={applyKioskTextEdit}
                  >
                    Apply
                  </button>
                </div>



              </div>
            </div>
          </div>
        )}

        {isKiosk && kioskUploadOpen && (
          <div className="kiosk-upload-overlay" role="dialog" aria-label="Upload">
            <div
              className={`kiosk-upload-panel ${(kioskUploadFlowActive && kioskUploadPhotoUrls.length === 0) ? 'qr-only' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={kioskUploadFileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onKioskUploadDeviceSelected}
              />
              <div className={`kiosk-upload-body ${(kioskUploadFlowActive && kioskUploadPhotoUrls.length === 0) ? 'qr-only' : ''}`}>
                {kioskUploadError ? (
                  <div className="kiosk-upload-error">{kioskUploadError}</div>
                ) : null}

                <div className="kiosk-upload-content">
                  {kioskUploadFlowActive && kioskUploadPhotoUrls.length === 0 ? (
                    <>
                      {kioskUploadTicket ? (
                        <div className="kiosk-upload-ticket">Ticket: {kioskUploadTicket}</div>
                      ) : null}
                      {kioskUploadQrUrl ? (
                        <img className="kiosk-upload-qr" src={kioskUploadQrUrl} alt="QR" />
                      ) : kioskUploadUploadUrl ? (
                        <div className="kiosk-upload-ticket">{kioskUploadUploadUrl}</div>
                      ) : null}
                      {kioskUploadLoading ? (
                        <div className="kiosk-upload-status">Creating ticket…</div>
                      ) : (
                        <div className="kiosk-upload-status">Waiting for upload…</div>
                      )}
                    </>
                  ) : (
                    (() => {
                      const combined = [...(Array.isArray(kioskUploadPhotoUrls) ? kioskUploadPhotoUrls : []), ...(Array.isArray(kioskUploadHistoryUrls) ? kioskUploadHistoryUrls : [])]
                        .map(u => String(u))
                        .filter(Boolean);
                      const list = Array.from(new Set(combined));
                      if (list.length === 0) {
                        return (
                          <div className="kiosk-upload-empty">
                            <div className="kiosk-upload-empty-title">No images uploaded yet</div>
                            <div className="kiosk-upload-empty-subtitle">Upload from device or scan QR to upload.</div>
                          </div>
                        );
                      }
                      return (
                        <div className="kiosk-upload-photos">
                          {list.map((u) => (
                            <button
                              key={u}
                              type="button"
                              className="kiosk-upload-photo"
                              onClick={async () => {
                                try {
                                  const c = controllerRef.current;
                                  if (!c || typeof c.addImage !== 'function') return;

                                  let urlToUse = u;
                                  let objectUrl = '';
                                  const isDataUrl = typeof u === 'string' && u.startsWith('data:');
                                  try {
                                    if (!isDataUrl) {
                                      const res = await fetch(u, { method: 'GET' });
                                      if (res && res.ok) {
                                        const blob = await res.blob();
                                        objectUrl = URL.createObjectURL(blob);
                                        urlToUse = objectUrl;
                                      }
                                    }
                                  } catch {
                                    // fall back to direct URL
                                  }

                                  try {
                                    const imageObj = await c.addImage(urlToUse, {
                                      name: 'uploaded-image',
                                      draggable: true,
                                      fitToStage: true,
                                      imageCrossOrigin: false,
                                    });

                                    // Persist the original remote URL in node attrs so exports/restores
                                    // don't store an ephemeral blob: URL.
                                    try {
                                      if (imageObj && typeof imageObj.setAttr === 'function') {
                                        imageObj.setAttr('imageUrl', String(u));
                                        imageObj.setAttr('src', String(u));
                                      } else if (imageObj && imageObj.attrs) {
                                        imageObj.attrs.imageUrl = String(u);
                                        imageObj.attrs.src = String(u);
                                      }
                                    } catch {}

                                    try {
                                      const stage = c.stage;
                                      if (imageObj && stage && typeof stage.width === 'function' && typeof stage.height === 'function') {
                                        const stageW = stage.width();
                                        const stageH = stage.height();
                                        const maxW = stageW * 0.5;
                                        const baseW = typeof imageObj.width === 'function' ? imageObj.width() : null;
                                        if (baseW && baseW > 0) {
                                          // Only downscale if the image is larger than our max width.
                                          const scale = baseW > maxW ? (maxW / baseW) : 1;
                                          try {
                                            if (typeof imageObj.scaleX === 'function') imageObj.scaleX(scale);
                                            if (typeof imageObj.scaleY === 'function') imageObj.scaleY(scale);
                                          } catch {}
                                          const renderedW = baseW * scale;
                                          const baseH = typeof imageObj.height === 'function' ? imageObj.height() : 0;
                                          const renderedH = baseH * scale;
                                          try {
                                            if (typeof imageObj.x === 'function') imageObj.x((stageW - renderedW) / 2);
                                            if (typeof imageObj.y === 'function') imageObj.y((stageH - renderedH) / 2);
                                          } catch {}
                                          try { if (typeof imageObj.syncVisual === 'function') imageObj.syncVisual(); } catch {}
                                          try { imageObj.getLayer?.()?.batchDraw?.(); } catch {}
                                        }
                                      }
                                    } catch {}
                                    closeKioskUploadModal();
                                  } finally {
                                    try {
                                      if (objectUrl) URL.revokeObjectURL(objectUrl);
                                    } catch {}
                                  }
                                } catch {}
                              }}
                            >
                              <img src={u} alt="Uploaded" />
                            </button>
                          ))}
                        </div>
                      );
                    })()
                  )}
                </div>

                <div className="kiosk-upload-actions">
                  <button
                    type="button"
                    className="kiosk-upload-close"
                    onClick={closeKioskUploadModal}
                  >
                    Close
                  </button>

                  {kioskUploadFlowActive && kioskUploadPhotoUrls.length === 0 ? null : (
                    <button
                      type="button"
                      className="kiosk-upload-device"
                      onClick={() => {
                        try {
                          kioskUploadFileInputRef.current?.click?.();
                        } catch {}
                      }}
                      disabled={kioskUploadLoading}
                    >
                      Device
                    </button>
                  )}

                  {kioskUploadFlowActive && kioskUploadPhotoUrls.length === 0 ? null : (
                    <button
                      type="button"
                      className="kiosk-upload-now"
                      onClick={startKioskUploadNow}
                      disabled={kioskUploadLoading}
                    >
                      QR Upload
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Kiosk Sticker Panel */}
        {isKiosk && kioskStickerPanelOpen && (
          <div
            className="kiosk-sticker-panel-overlay"
            role="dialog"
            aria-label="Sticker selection"
          >
            <div
              ref={kioskStickerPanelRef}
              className="kiosk-sticker-panel"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => {
                e.stopPropagation();
                handleKioskThemeBannerPointerDown(e);
              }}
              onTouchMove={(e) => {
                e.stopPropagation();
                handleKioskThemeBannerPointerMove(e);
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                handleKioskThemeBannerPointerUp();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleKioskThemeBannerPointerDown(e);
              }}
              onMouseMove={(e) => {
                handleKioskThemeBannerPointerMove(e);
              }}
              onMouseUp={() => {
                handleKioskThemeBannerPointerUp();
              }}
              onMouseLeave={() => {
                handleKioskThemeBannerPointerUp();
              }}
            >
              {/* Theme Carousel */}
              {getKioskThemesWithContent().length > 0 && (
                <div className="kiosk-sticker-theme-carousel" aria-label="Theme banner carousel">
                  {(() => {
                    const themesWithContent = getKioskThemesWithContent();
                    const selectedIdx = getKioskSelectedThemeIndex();
                    const activeTheme = themesWithContent[selectedIdx] || themesWithContent[0];
                    if (!activeTheme) return null;
                    return (
                      <>
                        <button
                          className="kiosk-sticker-theme-nav-arrow prev"
                          onClick={handleKioskPrevThemeBanner}
                          disabled={selectedIdx <= 0}
                          aria-label="Previous theme"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 18l-6-6 6-6" />
                          </svg>
                        </button>

                        <div
                          className="kiosk-sticker-theme-banner"
                          onClick={() => handleKioskThemeSelect(activeTheme)}
                          onTouchStart={handleKioskThemeBannerPointerDown}
                          onTouchMove={handleKioskThemeBannerPointerMove}
                          onTouchEnd={handleKioskThemeBannerPointerUp}
                          onMouseDown={handleKioskThemeBannerPointerDown}
                          onMouseMove={handleKioskThemeBannerPointerMove}
                          onMouseUp={handleKioskThemeBannerPointerUp}
                          onMouseLeave={handleKioskThemeBannerPointerUp}
                          tabIndex={0}
                          role="button"
                          aria-label={activeTheme.ThemeName}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleKioskThemeSelect(activeTheme);
                            }
                          }}
                        >
                          {activeTheme.ThemeCover ? (
                            <img
                              src={activeTheme.ThemeCover}
                              alt={activeTheme.ThemeName}
                              className="kiosk-sticker-theme-banner-image pointer-hover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : activeTheme.Image ? (
                            <img
                              src={activeTheme.Image}
                              alt={activeTheme.ThemeName}
                              className="kiosk-sticker-theme-banner-image pointer-hover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : activeTheme.Icon ? (
                            <img
                              src={activeTheme.Icon}
                              alt={activeTheme.ThemeName}
                              className="kiosk-sticker-theme-banner-image pointer-hover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <div className="kiosk-sticker-theme-banner-placeholder">
                              <span className="kiosk-sticker-theme-banner-icon">🎨</span>
                              <div className="kiosk-sticker-theme-banner-name">{activeTheme.ThemeName || 'Theme'}</div>
                            </div>
                          )}
                        </div>

                        <button
                          className="kiosk-sticker-theme-nav-arrow next"
                          onClick={handleKioskNextThemeBanner}
                          disabled={selectedIdx >= themesWithContent.length - 1}
                          aria-label="Next theme"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </button>
                      </>
                    );
                  })()}
                </div>
              )}

              <div className="kiosk-sticker-panel-content">
                <div className="kiosk-sticker-grid">
                  {kioskLoadingStickers ? (
                    <div className="kiosk-stickers-loading">
                      <div className="kiosk-sticker-spinner"></div>
                      <p>Loading stickers...</p>
                    </div>
                  ) : kioskStickers.length === 0 ? (
                    <div className="kiosk-no-stickers-message">No stickers available</div>
                  ) : (
                    kioskStickers.map((stickerUrl, index) => (
                      <button
                        key={stickerUrl}
                        type="button"
                        className="kiosk-sticker-item"
                        onClick={() => addKioskStickerToCanvas(stickerUrl)}
                      >
                        <div className="kiosk-sticker-preview">
                          <img 
                            src={stickerUrl} 
                            alt={`Sticker ${index + 1}`} 
                            crossOrigin="anonymous" 
                            loading="lazy"
                          />
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="kiosk-sticker-panel-actions">
                <button
                  type="button"
                  className="kiosk-sticker-panel-bottom-close"
                  onClick={closeKioskStickerPanel}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div 
          ref={containerRef} 
          className="product-canvas"
          style={{ width: '100%', height: '100%' }}
        />
        {configData && configData.is3DProduct && (
          <ThreeDPreview 
            product={product} 
            baseUrl={baseUrl} 
            is3DProduct={configData.is3DProduct}
            selectedView={selectedView}
            selectedViewIndex={product?.Views ? product.Views.indexOf(selectedView) : 0}
            konvaController={controllerRef.current}
            externalShow={showThreeD}
          />
        )}
        {/* Desktop floating toolbox toolbar */}
        {!isMobile && !isKiosk && (
          <div
            className="floating-toolbox"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 120,
              background: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
              borderRadius: 10,
              padding: 6
            }}
          >
            <ToolboxTab 
              mode="floating" 
              onShowLayerModal={openLayerModal}
              onCloseLayerModal={closeLayerModal}
              onToggle3D={handleToggle3D}
              onAddToCart={handleAddToCart}
              konvaController={controllerRef.current}
              show3D={!!(configData && configData.is3DProduct)}
              isThreeDActive={showThreeD}
              isLayerOpen={showLayerModal}
              configData={configData}
            />
          </div>
        )}
        {showTestFeatures && !isKiosk && (
        <FloatingButtonGroup
          isButtonGroupCollapsed={isButtonGroupCollapsed}
          onToggleCollapse={toggleButtonGroupCollapse}
          onDownloadDesign={handlePreview}
          onAddToCart={handleAddToCart}
          onMoveLayerUp={() => handleMoveLayer('up')}
          onMoveLayerDown={() => handleMoveLayer('down')}
          onManageLayers={openLayerModal}
          onToggleHintLayer={handleToggleHintLayer}
          isMobile={isMobile}
        />
        )}
        
        {/* Exported Images Panel */}
        <ExportedImagesPanel 
          exportedImages={exportedImages}
          allViewsImages={allViewsImages}
          configData={configData || propConfigData}
          showExportedImages={showExportedImages}
          setShowExportedImages={setShowExportedImages}
          onAddToCart={startAddToCartWithExisting}
          isTestMode={showTestFeatures}
          isExporting={isExporting}
          statusMessage={statusMessage}
          panelMode={exportPanelMode}
          CustomPanelComponent={exportPanelMode === 'custom' ? CustomExportPanel : undefined}
        />
      </div>
      
      {/* Layer Modal */}
      {showLayerModal && controllerRef.current && (
        <LayerModal 
          isOpen={showLayerModal}
          onClose={closeLayerModal}
          controller={controllerRef.current}
        />
      )}
    </div>
  );
});

export default ProductView;
